import { Grid } from '@mui/material';
import Box from '@mui/material/Box';
import { businessData as businessDataAtom } from 'atoms';
import FormModal from 'common/FormModal';
import FormStyle from 'common/FormStyle';
import Loader from 'common/Loaders/Loader';
import ButtonLoader from 'common/Loaders/ButtonLoader';
import StickyActionFooter from 'common/StickyActionFooter';
import ButtonSecondary from 'common/ButtonSecondary';
import Button from 'common/Button';
import NewMember from 'emails/NewMember';
import AvailabilityForm from 'pages/Business/Provider/ProviderPopup/Partials/AvailabilityForm';
import ProfileForm from 'pages/Business/Provider/ProviderPopup/Partials/ProfileForm';
import ServiceForm from 'pages/Business/Provider/ProviderPopup/Partials/ServiceForm';
import ServicePopup from 'pages/Business/Service/ServicePopup';
import ReactDOMServer from 'react-dom/server';
import { toast } from 'react-toastify';
import { useRecoilState } from 'recoil';
import supabaseHandler from 'utils/SupaBase';
import { isObjectEmpty, sendEmail } from 'utils/methods';
import { toasterNotification } from 'utils/toaster';
import { 
    addProviderQualificationApi, 
    updateProviderQualificationApi,
    addProviderCertificateApi,
    updateProviderCertificateApi,
    addProviderEducationApi,
    updateProviderEducationApi,
    addProviderLicenseApi,
    updateProviderLicenseApi,
    addProviderSpecialityApi,
    updateProviderSpecialityApi,
    getProviderSpecialityApi,
    createProviderProfileApi,
    updateProviderProfileApi,
    updateProviderAvailabilityApi
} from 'SupabseHandler';

import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { convertToUTC } from 'utils/time';
import StatusBadge from 'common/StatusBadge';
import IncompleteServicesDialog from 'common/IncompleteServicesDialog';
import { useAuth } from 'context/AuthContext';
export default function ProviderPopup({
    open,
    onClose,
    getProviderList,
    selectedProvider,
    setSelectedProvider,
    formState,
    setFormState,
    businessId,
    loading,
    setProviderLoading,
}) {
    const { token } = useAuth();
    const [error, setError] = useState('');
    const [businessData] = useRecoilState(businessDataAtom);
    const [isPublishing, setIsPublishing] = useState(false);
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);

    const SendEmailToProvider = async (firstName, lastName, email, businessName, businessId) => {
        if (!token) {
            console.error('No authentication token found');
            return;
        }

        const emailContent = `
        <!DOCTYPE html>
        ${ReactDOMServer.renderToString(
            <NewMember
                memberName={firstName}
                lastName={lastName}
                businessName={businessName}
                email={email}
                businessId={businessId}
                isProvider={true}
            />,
        )}
    `;
        await sendEmail(token,email, 'Action Required: Finish Setting Up Your BeSeen Profile', ' ', emailContent);
    };
    // for posting data?.
    const formatProfileData = (data) => {
        const providerData = {
            business_id: businessId,
            first_name: data?.first_name,
            last_name: data?.last_name,
            bio: data?.bio,
            email: data?.email?.toLowerCase(),
            handle: createHandelString(data?.first_name, data?.last_name),
            status: data?.status,
            profile_url: data?.profile_url,
            sex: data?.gender,
            image_url: data?.image_url,
        };
        const specialityData = data?.categories?.map((d, index) => ({
            business_id: businessId,
            speciality: data?.speciality?.[index] ? data?.speciality?.[index] : null,
            category: data?.categories?.[index] ? data?.categories?.[index] : null,
            provider_id: selectedProvider?.id ?? providerId,
        }));
        const qualificationData = data?.qualification_title?.map((d) => ({
            business_id: businessId,
            qualification: d,
            provider_id: selectedProvider?.id ?? providerId,
        }));
        const licensesData = data?.license_number?.map((number, index) => ({
            license_number: number,
            state: data?.license_state?.[index] ?? '',
            year: data?.license_year?.[index] ?? null,
            provider_id: selectedProvider?.id ?? providerId,
            business_id: businessId,
        }));
        const educationData = data?.education_degree?.map((degree, index) => ({
            degree: degree,
            field_of_study: data?.education_field?.[index],
            institution_name: data?.education_institute?.[index],
            provider_id: selectedProvider?.id ?? providerId,
            business_id: businessId,
        }));
        const certificateData = data?.board_certification?.map((certificate, index) => ({
            certification: certificate,
            state: data?.board_certification_state?.[index] ?? '',
            year: data?.board_certification_year?.[index],
            provider_id: selectedProvider?.id ?? providerId,
            business_id: businessId,
        }));

        return { providerData, specialityData, qualificationData, licensesData, educationData, certificateData };
    };
    const [providerId, setProviderId] = useState(null);
    const createProvider = async ( data) => {
        // Step 1: Format the profile data
        let { providerData, specialityData, qualificationData, licensesData, educationData, certificateData } =
            formatProfileData(data);

        // Step 2: Create provider profile using backend API
        try {
            if (!token) {
                throw new Error('No authentication token found');
            }
            const provider = await createProviderProfileApi(providerData, token);
            
            if (!provider || !provider.id) {
                throw new Error('Failed to create provider: Invalid response from API');
            }
            
            const providerId = provider.id;
            setProviderId(providerId);

            // Step 3: Insert related data
            const insertPromises = [
                updateQualification( qualificationData, providerId),
                updateSpeciality( specialityData, providerId),
                updateLicense( licensesData, providerId),
                updateEducation( educationData, providerId),
                updateCertificate( certificateData, providerId),
            ];

            const results = await Promise.all(insertPromises);

            // Check for errors in any operation
            results.forEach((result) => {
                if (result?.error) {
                    throw new Error('Error inserting related data: ' + result.error.message);
                }
            });

            // Step 4: Set selected provider
            setSelectedProvider({ ...provider, ...data });
            setFormData({ ...provider, ...data });
        } catch (error) {
            throw new Error('Error creating provider: ' + error.message);
        }
    };
    const updateQualification = async ( qualificationData, providerId) => {
        // Format the data to ensure provider_id is set
        const formattedData = qualificationData?.map((item) => ({ ...item, provider_id: providerId })) || [];

        // Skip if no qualification data
        if (formattedData.length === 0) {
            return;
        }

        try {
            // Use POST for new providers (when selectedProvider doesn't exist yet)
            // Use PUT for existing providers (when selectedProvider.id exists and matches providerId)
            const isUpdate = selectedProvider?.id && selectedProvider.id === providerId;
            
            if (isUpdate) {
                // Update existing provider qualifications using PUT API
                await updateProviderQualificationApi(providerId, formattedData, token);
            } else {
                // Create new provider qualifications using POST API
                await addProviderQualificationApi(formattedData, token);
            }
        } catch (error) {
            console.error('Error updating qualifications:', error);
            const errorMessage = error.message || 'Failed to save qualifications';
            toasterNotification('error', '', errorMessage);
            // Don't throw - allow other operations to continue
        }
    };
    const updateSpeciality = async ( specialityData, providerId) => {
        // Format the data to ensure provider_id is set
        const formattedData = specialityData?.map((item) => ({ ...item, provider_id: providerId })) || [];

        // Skip if no speciality data
        if (formattedData.length === 0) {
            return;
        }

        try {
            // Use POST for new providers (when selectedProvider doesn't exist yet)
            // Use PUT for existing providers (when selectedProvider.id exists and matches providerId)
            const isUpdate = selectedProvider?.id && selectedProvider.id === providerId;
            
            if (isUpdate) {
                // Update existing provider specialities using PUT API
                await updateProviderSpecialityApi(providerId, formattedData, token);
            } else {
                // Create new provider specialities using POST API
                await addProviderSpecialityApi(formattedData, token);
            }
        } catch (error) {
            console.error('Error updating specialities:', error);
            const errorMessage = error.message || 'Failed to save specialities';
            toasterNotification('error', '', errorMessage);
            // Don't throw - allow other operations to continue
        }
    };
    const updateLicense = async ( licensesData, providerId) => {
        // Format the data to ensure provider_id is set and handle year field
        const formattedData = licensesData?.map((item) => ({
            ...item,
            year: item?.year ? item?.year : null,
            provider_id: providerId,
        })) || [];

        // Skip if no license data
        if (formattedData.length === 0) {
            return;
        }

        try {
            // Use POST for new providers (when selectedProvider doesn't exist yet)
            // Use PUT for existing providers (when selectedProvider.id exists and matches providerId)
            const isUpdate = selectedProvider?.id && selectedProvider.id === providerId;
            
            if (isUpdate) {
                // Update existing provider licenses using PUT API
                await updateProviderLicenseApi(providerId, formattedData, token);
            } else {
                // Create new provider licenses using POST API
                await addProviderLicenseApi(formattedData, token);
            }
        } catch (error) {
            console.error('Error updating licenses:', error);
            const errorMessage = error.message || 'Failed to save licenses';
            toasterNotification('error', '', errorMessage);
            // Don't throw - allow other operations to continue
        }
    };
    const updateEducation = async ( educationData, providerId) => {
        // Format the data to ensure provider_id is set
        const formattedData = educationData?.map((item) => ({ ...item, provider_id: providerId })) || [];

        // Skip if no education data
        if (formattedData.length === 0) {
            return;
        }

        try {
            // Use POST for new providers (when selectedProvider doesn't exist yet)
            // Use PUT for existing providers (when selectedProvider.id exists and matches providerId)
            const isUpdate = selectedProvider?.id && selectedProvider.id === providerId;
            
            if (isUpdate) {
                // Update existing provider education using PUT API
                await updateProviderEducationApi(providerId, formattedData, token);
            } else {
                // Create new provider education using POST API
                await addProviderEducationApi(formattedData, token);
            }
        } catch (error) {
            console.error('Error updating education:', error);
            const errorMessage = error.message || 'Failed to save education';
            toasterNotification('error', '', errorMessage);
            // Don't throw - allow other operations to continue
        }
    };
    const updateCertificate = async ( certificateData, providerId) => {
        // Format the data to ensure provider_id is set
        const formattedData = certificateData?.map((item) => ({ ...item, provider_id: providerId })) || [];

        // Skip if no certificate data
        if (formattedData.length === 0) {
            return;
        }

        try {
            // Use POST for new providers (when selectedProvider doesn't exist yet)
            // Use PUT for existing providers (when selectedProvider.id exists and matches providerId)
            const isUpdate = selectedProvider?.id && selectedProvider.id === providerId;
            
            if (isUpdate) {
                // Update existing provider certificates using PUT API
                await updateProviderCertificateApi(providerId, formattedData, token);
            } else {
                // Create new provider certificates using POST API
                await addProviderCertificateApi(formattedData, token);
            }
        } catch (error) {
            console.error('Error updating certificates:', error);
            const errorMessage = error.message || 'Failed to save certificates';
            toasterNotification('error', '', errorMessage);
            // Don't throw - allow other operations to continue
        }
    };

    const updateProfileSection = async ( data, isDraft) => {
        let { providerData, specialityData, qualificationData, licensesData, educationData, certificateData } =
            formatProfileData(data);

        //update provider table
        // Determine if sensitive fields changed (qualifications, specialty, license, board cert)
        // Prefer the original selectedProvider object as the baseline to avoid
        // capturing an empty snapshot before data loads
        const initial = selectedProvider && Object.keys(selectedProvider).length > 0
            ? selectedProvider
            : (() => {
                try {
                    return initialFormState ? JSON.parse(initialFormState) : {};
                } catch (_) {
                    return {};
                }
            })();

        const normalizeStringArray = (arr) => (Array.isArray(arr) ? arr.map(v => (v ?? '').toString().trim()) : []);
        const arraysEqualIgnoreOrder = (a, b) => {
            const aa = normalizeStringArray(a).filter(Boolean).sort();
            const bb = normalizeStringArray(b).filter(Boolean).sort();
            if (aa.length !== bb.length) return false;
            return aa.every((v, i) => v === bb[i]);
        };
        const tupleArray = (partsArrays) => {
            const maxLen = Math.max(...partsArrays.map(p => (Array.isArray(p) ? p.length : 0)));
            const tuples = [];
            for (let i = 0; i < maxLen; i++) {
                const parts = partsArrays.map(p => (Array.isArray(p) ? (p[i] ?? '') : ''));
                // Only include if there is at least one non-empty significant value
                if (parts.some(v => (v ?? '').toString().trim() !== '')) {
                    tuples.push(parts.map(v => (v ?? '').toString().trim()).join('|'));
                }
            }
            return tuples;
        };

        // Qualifications (titles)
        const currentQualifications = initial?.qualifications ?? initial?.qualification_title ?? [];
        const newQualifications = data?.qualification_title ?? [];
        const qualificationsChanged = !arraysEqualIgnoreOrder(currentQualifications, newQualifications);

        // Specialty (title + category) - robust detection using DB as source of truth
        let specialtiesChanged = false;
        try {
            // Fetch existing specialities from backend API
            const { data: existingSpecialitiesData } = await getProviderSpecialityApi(selectedProvider?.id);
            const existingSpecialities = existingSpecialitiesData || [];
            
            const normalize = (v) => (v ?? '').toString().trim().toLowerCase();
            const toKey = (o) => `${normalize(o?.speciality)}|${normalize(o?.category)}`;
            const existingKeys = new Set(
                (existingSpecialities || [])
                    .filter((r) => normalize(r?.speciality) !== '' || normalize(r?.category) !== '')
                    .map(toKey)
            );
            const newKeys = new Set(
                (specialityData || [])
                    .filter((r) => normalize(r?.speciality) !== '' || normalize(r?.category) !== '')
                    .map(toKey)
            );
            if (existingKeys.size !== newKeys.size) {
                specialtiesChanged = true;
            } else {
                for (const k of existingKeys) {
                    if (!newKeys.has(k)) {
                        specialtiesChanged = true;
                        break;
                    }
                }
            }
        } catch (e) {
            // Fallback to tuple comparison using initial snapshot if DB check fails
            const currentSpecialityTitles = (initial?.specialties && initial?.specialties.length > 0)
                ? initial?.specialties
                : initial?.speciality;
            const currentCategories = initial?.categories;
            const newSpecialityTitles = data?.speciality;
            const newCategories = data?.categories;
            const currentSpecialtiesTuples = tupleArray([currentSpecialityTitles || [], currentCategories || []]).sort();
            const newSpecialtiesTuples = tupleArray([newSpecialityTitles || [], newCategories || []]).sort();
            specialtiesChanged = (() => {
                if (currentSpecialtiesTuples.length !== newSpecialtiesTuples.length) return true;
                return !currentSpecialtiesTuples.every((v, i) => v === newSpecialtiesTuples[i]);
            })();
        }

        // Licenses (number + state + year)
        const currentLicensesTuples = tupleArray([
            initial?.license_number || [],
            initial?.license_state || [],
            initial?.license_year || [],
        ]).sort();
        const newLicensesTuples = tupleArray([
            data?.license_number || [],
            data?.license_state || [],
            data?.license_year || [],
        ]).sort();
        const licensesChanged = (() => {
            if (currentLicensesTuples.length !== newLicensesTuples.length) return true;
            return !currentLicensesTuples.every((v, i) => v === newLicensesTuples[i]);
        })();

        // Board Certifications (cert + state + year)
        const currentCertsTuples = tupleArray([
            initial?.board_certification || [],
            initial?.board_certification_state || [],
            initial?.board_certification_year || [],
        ]).sort();
        const newCertsTuples = tupleArray([
            data?.board_certification || [],
            data?.board_certification_state || [],
            data?.board_certification_year || [],
        ]).sort();
        const certsChanged = (() => {
            if (currentCertsTuples.length !== newCertsTuples.length) return true;
            return !currentCertsTuples.every((v, i) => v === newCertsTuples[i]);
        })();

        const requireReapproval = qualificationsChanged || specialtiesChanged || licensesChanged || certsChanged;

        // Prepare update data with approval status if needed
        const updateData = { ...providerData };
        if (!isDraft && requireReapproval) {
            updateData.is_approved = false;
            updateData.approval_status = 'pending';
        }

        // Update provider profile using backend API
        try {
            if (!token) {
                throw new Error('No authentication token found');
            }
            const provider = await updateProviderProfileApi(selectedProvider?.id, updateData, token);
            
            if (!provider) {
                throw new Error('Failed to update provider: Invalid response from API');
            }

            // Update related data
            await Promise.all([
                updateQualification( qualificationData, selectedProvider?.id),
                updateSpeciality( specialityData, selectedProvider?.id),
                updateLicense( licensesData, selectedProvider?.id),
                updateEducation( educationData, selectedProvider?.id),
                updateCertificate( certificateData, selectedProvider?.id),
            ]);
        } catch (error) {
            throw new Error('Error updating provider profile: ' + error.message);
        }
    };

// Updated helper functions with more precise comparison
const checkEducationChanges = (currentEducation, newEducation) => {
    // If counts are different, definitely changed
    if (currentEducation.length !== newEducation.length) return true;
    
    // Create comparable objects for each education record
    const normalizeEducation = (edu) => ({
        degree: edu?.degree || '',
        institution_name: edu?.institution_name || '',
        field_of_study: edu?.field_of_study || '',
        year: edu?.year || ''
    });

    const currentNormalized = currentEducation.map(normalizeEducation).sort((a, b) => 
        `${a.degree}-${a.institution_name}`.localeCompare(`${b.degree}-${b.institution_name}`)
    );
    
    const newNormalized = newEducation.map(normalizeEducation).sort((a, b) => 
        `${a.degree}-${a.institution_name}`.localeCompare(`${b.degree}-${b.institution_name}`)
    );

    // Deep compare each education record
    return !currentNormalized.every((edu, index) => 
        JSON.stringify(edu) === JSON.stringify(newNormalized[index])
    );
};

const checkQualificationChanges = (currentQualifications, newQualifications) => {
    if (currentQualifications.length !== newQualifications.length) return true;
    
    // Compare sorted lists of qualifications
    const currentSorted = currentQualifications
        .map(q => q?.qualification?.trim() || '')
        .sort();
    
    const newSorted = newQualifications
        .map(q => q?.qualification?.trim() || '')
        .sort();

    return !currentSorted.every((qual, index) => qual === newSorted[index]);
};

const checkCertificateChanges = (currentCertificates, newCertificates) => {
    if (currentCertificates.length !== newCertificates.length) return true;
    
    // Create comparable objects for each certificate
    const normalizeCert = (cert) => ({
        certification: cert?.certification || '',
        year: cert?.year || '',
        state: cert?.state || ''
    });

    const currentNormalized = currentCertificates.map(normalizeCert).sort((a, b) => 
        `${a.certification}-${a.year}`.localeCompare(`${b.certification}-${b.year}`)
    );
    
    const newNormalized = newCertificates.map(normalizeCert).sort((a, b) => 
        `${a.certification}-${a.year}`.localeCompare(`${b.certification}-${b.year}`)
    );

    // Deep compare each certificate
    return !currentNormalized.every((cert, index) => 
        JSON.stringify(cert) === JSON.stringify(newNormalized[index])
    );
};
    const updateServiceSection = async ( data, isDraft) => {
 
        // Safely ensure arrays exist before mapping
        const services = Array.isArray(data?.services) ? data.services : [];
        const servicePrices = Array.isArray(data?.service_price) ? data.service_price : [];
        const serviceDurations = Array.isArray(data?.service_duration) ? data.service_duration : [];
        const serviceTelemedecines = Array.isArray(data?.service_telemedecine) ? data.service_telemedecine : [];
        const serviceBookables = Array.isArray(data?.service_bookable) ? data.service_bookable : [];
        const serviceVisitModes = Array.isArray(data?.service_visit_mode) ? data.service_visit_mode : [];
        
        
        // Convert service IDs to numbers for database (they come as strings from form)
        // Map services with their corresponding data by index
        // When visit_mode is 'both', create TWO entries (one for in-person, one for telemedicine)
        const serviceData = services
            .flatMap((service, index) => {
                // Skip empty, invalid, or placeholder services
                if (!service || service === '' || service === 'add_new_service') {
                    return [];
                }
                
                const visitMode = serviceVisitModes[index] || 
                    (serviceTelemedecines[index] ? 'both' : 'in_person');
                
                const baseServiceData = {
                    service_id: Number(service), // Convert string to number for database
                    price: parseFloat(servicePrices[index]) || 0,
                    duration: parseInt(serviceDurations[index]) || 0,
                    provider_id: data?.id || selectedProvider?.id,
                    bookable: serviceBookables[index] !== undefined ? serviceBookables[index] : true,
                    status: data?.status,
                };
                
                // Handle different visit modes
                if (visitMode === 'both') {
                    // Create TWO entries: one for in-person, one for telemedicine
                    return [
                        {
                            ...baseServiceData,
                            is_telemedicine: false,
                            telemedicine: false,
                        },
                        {
                            ...baseServiceData,
                            is_telemedicine: true,
                            telemedicine: true,
                        }
                    ];
                } else if (visitMode === 'in_person') {
                    // Create one entry for in-person only
                    return [{
                        ...baseServiceData,
                        is_telemedicine: false,
                        telemedicine: false,
                    }];
                } else if (visitMode === 'telemedicine') {
                    // Create one entry for telemedicine only
                    return [{
                        ...baseServiceData,
                        is_telemedicine: true,
                        telemedicine: true,
                    }];
                } else if (visitMode === 'not_available') {
                    // Create one entry with bookable: false
                    return [{
                        ...baseServiceData,
                        is_telemedicine: false,
                        telemedicine: false,
                        bookable: false,
                    }];
                } else {
                    // Fallback: use the old logic based on telemedicine flag
                    return [{
                        ...baseServiceData,
                        is_telemedicine: serviceTelemedecines[index] || false,
                        telemedicine: serviceTelemedecines[index] || false,
                    }];
                }
            });
            

        setFormData({
            ...data,
            service_telemedecine: Array.isArray(data?.service_telemedecine) ? data.service_telemedecine : [],
            service_bookable: Array.isArray(data?.service_bookable) ? data.service_bookable : [],
        });

        // Ensure consultation details are properly formatted (numbers for duration/price, string for URL)
        // Use 0 instead of null for empty numeric values to avoid database constraint issues
        // CRITICAL: Check consulationDetails STATE first, then fallback to data
        // Prices are now stored in the DB as decimal (float8), NOT as integer cents.
        const inPersonPrice = consulationDetails?.in_person_consultation_price !== undefined 
            ? parseFloat(consulationDetails.in_person_consultation_price) 
            : (data?.in_person_consultation_price ? parseFloat(data.in_person_consultation_price) : 0);
        const telemedicinePrice = consulationDetails?.telemedicine_consultation_price !== undefined 
            ? parseFloat(consulationDetails.telemedicine_consultation_price) 
            : (data?.telemedicine_consultation_price ? parseFloat(data.telemedicine_consultation_price) : 0);
        
        const normalizePrice = (price) => {
            const num = Number(price);
            if (Number.isNaN(num)) {
                return 0;
            }
            // Store with max 2 decimal places
            return Number(num.toFixed(2));
        };

        const consultationDetails = {
            in_person_consultation_duration: consulationDetails?.in_person_consultation_duration !== undefined 
                ? Number(consulationDetails.in_person_consultation_duration) 
                : (data?.in_person_consultation_duration ? Number(data.in_person_consultation_duration) : 0),
            in_person_consultation_price: normalizePrice(inPersonPrice),
            telemedicine_consultation_duration: consulationDetails?.telemedicine_consultation_duration !== undefined 
                ? Number(consulationDetails.telemedicine_consultation_duration) 
                : (data?.telemedicine_consultation_duration ? Number(data.telemedicine_consultation_duration) : 0),
            telemedicine_consultation_price: normalizePrice(telemedicinePrice),
            telemedicine_url: consulationDetails?.telemedicine_url || data?.telemedicine_url || '',
            exclude_FMP_inperson: data?.exclude_FMP_inperson === true || consulationDetails?.exclude_FMP_inperson === true,
            exclude_FMP_telemedicine: data?.exclude_FMP_telemedicine === true || consulationDetails?.exclude_FMP_telemedicine === true,
        };
        

        // If telemedicine is disabled (indicated by all service_telemedecine flags being false
        // and consultation values being 0), explicitly set URL to empty string
        const isTelemedicineDisabled = 
            (!data?.telemedicine_consultation_duration || data.telemedicine_consultation_duration === 0) && 
            (!data?.telemedicine_consultation_price || data.telemedicine_consultation_price === 0) && 
            (!serviceTelemedecines.some(flag => flag === true));
            
        if (isTelemedicineDisabled) {
            consultationDetails.telemedicine_url = '';
        }

        const providerId = data?.id || selectedProvider?.id;
        
        
        if (!providerId) {
            console.error('Error: No provider ID found for updating consultation details');
            return;
        }
        
        const updatePayload = (() => {
            if (isDraft) {
                return { ...consultationDetails };
            }
            if (formState === 'add') {
                return {
                    ...consultationDetails,
                    status: 'In Review',
                    is_approved: false,
                    approval_status: 'pending',
                };
            }
            // Edit mode: do not alter approval fields; only update status
            return {
                ...consultationDetails,
                status: 'In Review',
            };
        })();
        
        
        try {
            if (!token) {
                throw new Error('No authentication token found');
            }
            const updateData = await updateProviderProfileApi(providerId, updatePayload, token);
        } catch (updateError) {
            console.error('❌ Error updating provider consultation details:', updateError);
            console.error('Error details:', updateError?.message || JSON.stringify(updateError, null, 2));
            console.error('Update payload that failed:', JSON.stringify(updatePayload, null, 2));
            console.error('Provider ID:', providerId);
            throw updateError;
        }

        // Save provider services via backend API (replaces old delete + insert Supabase calls)
        if (providerId) {
            try {
                if (!token) {
                    throw new Error('No authentication token found');
                }

                const servicesPayload = {
                    business_id: businessId,
                    services: serviceData,
                };


                const response = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${providerId}/services`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify(servicesPayload),
                    },
                );

                const result = await response.json();

                if (!response.ok || result.error) {
                    const errorMessage = result.error || 'Failed to save provider services';
                    console.error('Error saving provider services via backend:', errorMessage);
                    throw new Error(errorMessage);
                }

            } catch (error) {
                console.error('❌ Error in backend provider services update:', error);
                throw error;
            }
        } else {
            console.error('No providerId available for service save! Service data will not be persisted.', {
                serviceDataLength: serviceData.length,
            });
        }
    };
    const updateAvailability = async ( data, isDraft) => {
        // Get provider ID
        const providerId = data?.id || selectedProvider?.id;
        
        if (!providerId) {
            throw new Error('Provider ID is required');
        }

        // Prepare availability data payload for backend API
        // The backend handles all transformation, deletion, and insertion
        const availabilityPayload = {
            business_id: businessId,
            status: data?.status || (isDraft ? 'draft' : 'Active'),
            availability_day: Array.isArray(data?.availability_day) ? data.availability_day : [],
            availability_start_time: Array.isArray(data?.availability_start_time) ? data.availability_start_time : [],
            availability_end_time: Array.isArray(data?.availability_end_time) ? data.availability_end_time : [],
            availability_location: Array.isArray(data?.availability_location) ? data.availability_location : [],
            telemedicine: Array.isArray(data?.telemedicine) ? data.telemedicine : [],
        };

        try {
            if (!token) {
                throw new Error('No authentication token found');
            }
            // Call the backend API to update availability
            // The backend handles:
            // - Deleting existing availability
            // - Updating provider status
            // - Transforming and inserting new availability records
            await updateProviderAvailabilityApi(providerId, availabilityPayload, token, formState);

            // Send email if needed (for new providers published, not drafts)
            if (formState === 'add' && data?.status !== 'draft' && data?.first_name && data?.email) {
                await SendEmailToProvider(
                    data?.first_name,
                    data?.last_name,
                    data?.email,
                    businessData?.name,
                    businessData?.id,
                );
            }
        } catch (error) {
            console.error('Error updating provider availability:', error);
            throw error;
        }
    };

    const updateProvider = async ( data, section, isDraft) => {
        try {
            if (section === 'profile') {
                return await updateProfileSection( data, isDraft);
            }

            if (section === 'services') {
                return await updateServiceSection( data, isDraft);
            }

            if (section === 'availability') {
                return await updateAvailability( data, isDraft);
            }
        } catch (error) {
            console.error('Update Provider Error:', error);
            throw error;
        }
    };

    // Check for incomplete services (missing service name)
    // This function now checks ALL service-related arrays to find entries where other fields are filled but service name is missing
    const checkForIncompleteServices = (servicesArray, servicePrices = [], serviceDurations = [], serviceVisitModes = []) => {
        if (!Array.isArray(servicesArray)) {
            return [];
        }
        
        const incompleteIndices = [];
        // Find the maximum length across all arrays to check all potential service entries
        const maxLength = Math.max(
            servicesArray.length,
            servicePrices.length || 0,
            serviceDurations.length || 0,
            serviceVisitModes.length || 0
        );
        
        for (let index = 0; index < maxLength; index++) {
            const service = servicesArray[index];
            const hasPrice = servicePrices[index] && servicePrices[index] !== '' && servicePrices[index] !== '0.00';
            const hasDuration = serviceDurations[index] && serviceDurations[index] !== '' && serviceDurations[index] !== '0';
            const hasVisitMode = serviceVisitModes[index] && serviceVisitModes[index] !== '';
            
            // Check if service name is missing but other fields are filled
            const isServiceNameMissing = !service || service === '' || service === 'add_new_service';
            const hasOtherFields = hasPrice || hasDuration || hasVisitMode;
            
            // If service name is missing BUT other fields are filled, it's incomplete
            if (isServiceNameMissing && hasOtherFields) {
                incompleteIndices.push(index);
            }
        }
        return incompleteIndices;
    };

    // Check if we should prevent navigation due to incomplete services
    const checkAndHandleIncompleteServices = (onProceed) => {
        // Only check when we're on the services tab (value === 1)
        if (value !== 1) {
            onProceed();
            return;
        }

        // Get current services data from all arrays
        const servicesFromAdditional = additionalMultipleFields?.services;
        const servicesFromFormData = formData?.services;
        const services = Array.isArray(servicesFromAdditional) 
            ? servicesFromAdditional 
            : (Array.isArray(servicesFromFormData) ? servicesFromFormData : []);
        
        const servicePrices = Array.isArray(additionalMultipleFields?.service_price) 
            ? additionalMultipleFields.service_price 
            : (Array.isArray(formData?.service_price) ? formData.service_price : []);
        
        const serviceDurations = Array.isArray(additionalMultipleFields?.service_duration) 
            ? additionalMultipleFields.service_duration 
            : (Array.isArray(formData?.service_duration) ? formData.service_duration : []);
        
        const serviceVisitModes = Array.isArray(additionalMultipleFields?.service_visit_mode) 
            ? additionalMultipleFields.service_visit_mode 
            : (Array.isArray(formData?.service_visit_mode) ? formData.service_visit_mode : []);
        
        const incompleteIndices = checkForIncompleteServices(services, servicePrices, serviceDurations, serviceVisitModes);
        
        if (incompleteIndices.length > 0) {
            // Store the callback to proceed after user chooses
            setPendingNavigationCallback(() => onProceed);
            setIncompleteServicesIndices(incompleteIndices);
            showIncompleteServicesDialogRef.current = true;
            setShowIncompleteServicesDialog(true);
            return false; // Prevent navigation
        }
        
        onProceed();
        return true; // Allow navigation
    };

    // Helper function to actually proceed with submission
    const proceedWithSubmission = async (data, doRest) => {
        // Prevent submission if dialog is open
        if (showIncompleteServicesDialog) {
            setIsPublishing(false);
            return;
        }
        
        // Determine section robustly: prefer explicit flag; fallback to current tab
        const inferredSection = data?.form_section
            || (value === 0 ? 'profile' : value === 1 ? 'services' : value === 2 ? 'availability' : '');

        if (inferredSection === 'services') {
            // Safely check if services array exists and has items
            const services = Array.isArray(data?.services) ? data.services : [];
            const servicePrices = Array.isArray(data?.service_price) ? data.service_price : [];
            const serviceDurations = Array.isArray(data?.service_duration) ? data.service_duration : [];
            const serviceVisitModes = Array.isArray(data?.service_visit_mode) ? data.service_visit_mode : [];
            
            // Check for duplicate services based on service ID + price + duration + visit mode
            // Same service ID is allowed if price, duration, or visit mode differs
            const serviceCombinations = services.map((serviceId, index) => {
                if (!serviceId || serviceId === '' || serviceId === 'add_new_service') {
                    return null; // Skip empty/invalid services
                }
                return {
                    serviceId: String(serviceId),
                    price: servicePrices[index] || '',
                    duration: serviceDurations[index] || '',
                    visitMode: serviceVisitModes[index] || ''
                };
            }).filter(Boolean); // Remove null entries
            
            // Create a set of unique combinations (as strings for comparison)
            const uniqueCombinations = new Set(
                serviceCombinations.map(combo => 
                    `${combo.serviceId}|${combo.price}|${combo.duration}|${combo.visitMode}`
                )
            );
            
            // If there are duplicates (same service ID with same price, duration, and visit mode)
            if (serviceCombinations.length > 0 && serviceCombinations.length !== uniqueCombinations.size) {
                toasterNotification('info', '', 'Services must be different to proceed');
                setIsPublishing(false);
                return false;
            }

            // Validate service prices must be > 1
            const invalidPriceIndex = services.findIndex((serviceId, index) => {
                if (!serviceId || serviceId === '' || serviceId === 'add_new_service') return false;
                const price = servicePrices[index];
                if (price === null || price === undefined || price === '') return false;
                const priceNum = parseFloat(price);
                return !isNaN(priceNum) && priceNum <= 1;
            });
            if (invalidPriceIndex >= 0) {
                toasterNotification('info', '', 'Service price must be greater than 1.');
                setIsPublishing(false);
                return false;
            }

            // Validate consultation prices (In-person & Telemedicine) must be $25 or above when enabled
            const consultationsEnabledForValidation = !!(consultationCheckboxes?.addInPerson || consultationCheckboxes?.addTelemedicine);
            if (consultationsEnabledForValidation) {
                const inPersonPrice = consulationDetails?.in_person_consultation_price !== undefined
                    ? parseFloat(consulationDetails.in_person_consultation_price)
                    : (data?.in_person_consultation_price != null && data?.in_person_consultation_price !== '' ? parseFloat(data.in_person_consultation_price) : null);
                const telemedicinePrice = consulationDetails?.telemedicine_consultation_price !== undefined
                    ? parseFloat(consulationDetails.telemedicine_consultation_price)
                    : (data?.telemedicine_consultation_price != null && data?.telemedicine_consultation_price !== '' ? parseFloat(data.telemedicine_consultation_price) : null);

                const inPersonInvalid = consultationCheckboxes?.addInPerson && (inPersonPrice === null || isNaN(inPersonPrice) || inPersonPrice < 25);
                const telemedicineInvalid = consultationCheckboxes?.addTelemedicine && (telemedicinePrice === null || isNaN(telemedicinePrice) || telemedicinePrice < 25);
                if (inPersonInvalid || telemedicineInvalid) {
                    toasterNotification('info', '', 'Consultation price must be greater than $25.');
                    setConsultationPriceErrors({ inPerson: !!inPersonInvalid, telemedicine: !!telemedicineInvalid });
                    setIsPublishing(false);
                    return false;
                }
            }

            // CRITICAL: Merge consultation details - prioritize consulationDetails state over incoming data
            const finalConsultationDetails = {
                in_person_consultation_duration:
                    consulationDetails?.in_person_consultation_duration !== undefined
                        ? consulationDetails.in_person_consultation_duration
                        : (data?.in_person_consultation_duration || formData?.in_person_consultation_duration),
                in_person_consultation_price:
                    consulationDetails?.in_person_consultation_price !== undefined
                        ? consulationDetails.in_person_consultation_price
                        : (data?.in_person_consultation_price || formData?.in_person_consultation_price),
                telemedicine_consultation_duration:
                    consulationDetails?.telemedicine_consultation_duration !== undefined
                        ? consulationDetails.telemedicine_consultation_duration
                        : (data?.telemedicine_consultation_duration || formData?.telemedicine_consultation_duration || 0),
                telemedicine_consultation_price:
                    consulationDetails?.telemedicine_consultation_price !== undefined
                        ? consulationDetails.telemedicine_consultation_price
                        : (data?.telemedicine_consultation_price || formData?.telemedicine_consultation_price || 0),
                telemedicine_url:
                    consulationDetails?.telemedicine_url ||
                    data?.telemedicine_url ||
                    formData?.telemedicine_url ||
                    '',
                exclude_FMP_inperson: consulationDetails?.exclude_FMP_inperson === true || data?.exclude_FMP_inperson === true || formData?.exclude_FMP_inperson === true,
                exclude_FMP_telemedicine: consulationDetails?.exclude_FMP_telemedicine === true || data?.exclude_FMP_telemedicine === true || formData?.exclude_FMP_telemedicine === true,
            };

            data = {
                ...data,
                ...finalConsultationDetails,
            };
            data['status'] = selectedProvider?.status;
            
            // Determine whether consultations are enabled via checkboxes.
            const consultationsEnabled = !!(consultationCheckboxes?.addInPerson || consultationCheckboxes?.addTelemedicine);

            updateProvider( data, 'services', doRest === 'draft').then(async (r) => {
                
                // Prevent navigation if dialog is open
                if (showIncompleteServicesDialog) {
                    setIsPublishing(false);
                    return;
                }
                
                if (doRest) {
                    resetForm();
                } else if (consultationsEnabled) {
                    // Move to Availability step when consultations are enabled
                    setValueSafe(2);
                    setDisableAvailability(false);
                } else {
                    // No consultations enabled -> finalise without Availability,
                    // but keep the provider in 'In Review' status (not 'Active')
                    try {
                        const finalData = { ...data, status: 'In Review' };
                        await updateProvider( finalData, 'availability', false);
                        await onClose();
                        await resetForm();
                    } catch (e) {
                        console.error('Error finalising provider from services step:', e);
                    }
                }
                setIsPublishing(false);
            }).catch((error) => {
                console.error('Error updating services:', error);
                setIsPublishing(false);
            });
        }
    };

    const handleSubmit = async (e, doRest) => {
        try {
 
            setIsPublishing(true);
            setAttemptedSubmit(true);
            e.preventDefault();
            
            // Skip validation if in edit mode
            if (formState !== 'edit' && !isFormValid) {
 
                setError('Please fill in all required fields.');
                setIsPublishing(false);
                return;
            }
            
            if (formState !== 'edit' && !formData.image_url) {
                setError('Please upload image.');
                setIsPublishing(false);
                return;
            }

 
 

            let data = removeAllEmptyStringValueAndArray({ ...formData, ...additionalMultipleFields });
            
            
            // Ensure services data is explicitly included if it exists in additionalMultipleFields
            // IMPORTANT: Always include services from additionalMultipleFields, even if empty arrays
            if (additionalMultipleFields?.form_section === 'services') {
                data.services = additionalMultipleFields.services || [];
                data.service_price = additionalMultipleFields.service_price || [];
                data.service_duration = additionalMultipleFields.service_duration || [];
                data.service_telemedecine = additionalMultipleFields.service_telemedecine || [];
                data.service_bookable = additionalMultipleFields.service_bookable || [];
                data.service_visit_mode = additionalMultipleFields.service_visit_mode || [];
            }
            
            setFormData({ ...formData, ...additionalMultipleFields });

            data['status'] = 'draft';
            

            // Determine section robustly: prefer explicit flag; fallback to current tab
            const inferredSection = data?.form_section
                || (value === 0 ? 'profile' : value === 1 ? 'services' : value === 2 ? 'availability' : '');

            // Check for incomplete services if we're on the services section
            if (inferredSection === 'services') {
                const services = Array.isArray(data?.services) ? data.services : [];
                const servicePrices = Array.isArray(data?.service_price) ? data.service_price : [];
                const serviceDurations = Array.isArray(data?.service_duration) ? data.service_duration : [];
                const serviceVisitModes = Array.isArray(data?.service_visit_mode) ? data.service_visit_mode : [];
                const incompleteIndices = checkForIncompleteServices(services, servicePrices, serviceDurations, serviceVisitModes);
                
                
                if (incompleteIndices.length > 0) {
                    // Store the data and show dialog
                    setPendingSubmitData(data);
                    setPendingSubmitDoRest(doRest);
                    setIncompleteServicesIndices(incompleteIndices);
                    showIncompleteServicesDialogRef.current = true;
                    setShowIncompleteServicesDialog(true);
                    setIsPublishing(false);
                    return false; // CRITICAL: Prevent proceedWithSubmission and tab change
                }
            }

            if (inferredSection === 'profile') {
                if (!data?.first_name || !data?.last_name || !data?.email) {
 
                    setIsPublishing(false);
                    return;
                }

                if (formState === 'edit') {
                    data['status'] = selectedProvider?.status;

                    try {
                        await updateProvider( data, 'profile', doRest === true);
                        if (doRest) {
                            resetForm();
                        } else {
                            setValue(1);
                            // Enable services tab after profile is successfully saved
                            setDisableService(false);
                        }
                    } catch (err) {
                        console.error('Error updating provider profile:', err);
                        const msg = err?.message || 'Failed to update provider profile';
                        setError(msg);
                        toasterNotification('error', '', msg);
                        setIsPublishing(false);
                        return;
                    }
                } else {
                    try {
                        await createProvider(data);
                        if (doRest) {
                            resetForm();
                        } else {
                            setValue(1);
                            // Enable services tab after profile is successfully saved
                            setDisableService(false);
                        }
                    } catch (err) {
                        console.error('Error creating provider profile:', err);
                        const msg = err?.message || 'Failed to create provider profile';
                        setError(msg);
                        toasterNotification('error', '', msg);
                        setIsPublishing(false);
                        return;
                    }
                }

                return;
            }

            if (selectedProvider === null) {
 
                setIsPublishing(false);
                return;
            }

            if (inferredSection === 'services') {
                const success = await proceedWithSubmission(data, doRest);
                if (success === false) return false;
            }

            if (inferredSection === 'availability') {
                // In edit mode: send 'In Review' only if provider changed specialty, qualification, education, license, or certification; otherwise preserve current status.
                // In add mode: submit for review. Draft handling is managed separately via handleSaveDraft.
                data['status'] =
                    formState === 'edit'
                        ? (checkIfProfileCredentialsChanged ? 'In Review' : (selectedProvider?.status ?? data?.status ?? 'In Review'))
                        : 'In Review';
                updateProvider( data, 'availability', false)
                    .then(async (r) => {

                        await onClose();
                        await resetForm();
                    })
                    .catch((error) => {
                        console.error('Failed to update availability:', error);
                    });
            }
        } catch (error) {
 
        } finally {
 
            setTimeout(() => setIsPublishing(false), 500);
        }
    };

    const resetForm = () => {
        setValue(0);
        setDisableService(true);
        setDisableAvailability(true);

        setFormState('add');
        setSelectedProvider({});
        setFormData({});
        setConsulationDetails({});
        setAttemptedSubmit(false);
        setError('');

        getProviderList().then();
    };

    // Handle removing incomplete services
    const handleRemoveIncompleteServices = () => {
        
        // Get current service arrays from state
        const services = Array.isArray(additionalMultipleFields?.services) 
            ? [...additionalMultipleFields.services] 
            : (Array.isArray(formData?.services) ? [...formData.services] : []);
        const servicePrices = Array.isArray(additionalMultipleFields?.service_price) 
            ? [...additionalMultipleFields.service_price] 
            : (Array.isArray(formData?.service_price) ? [...formData.service_price] : []);
        const serviceDurations = Array.isArray(additionalMultipleFields?.service_duration) 
            ? [...additionalMultipleFields.service_duration] 
            : (Array.isArray(formData?.service_duration) ? [...formData.service_duration] : []);
        const serviceTelemedecines = Array.isArray(additionalMultipleFields?.service_telemedecine) 
            ? [...additionalMultipleFields.service_telemedecine] 
            : (Array.isArray(formData?.service_telemedecine) ? [...formData.service_telemedecine] : []);
        const serviceBookables = Array.isArray(additionalMultipleFields?.service_bookable) 
            ? [...additionalMultipleFields.service_bookable] 
            : (Array.isArray(formData?.service_bookable) ? [...formData.service_bookable] : []);
        const serviceVisitModes = Array.isArray(additionalMultipleFields?.service_visit_mode) 
            ? [...additionalMultipleFields.service_visit_mode] 
            : (Array.isArray(formData?.service_visit_mode) ? [...formData.service_visit_mode] : []);
        
        // Remove items in reverse order to maintain correct indices
        const sortedIndices = [...incompleteServicesIndices].sort((a, b) => b - a);
        sortedIndices.forEach(index => {
            services.splice(index, 1);
            servicePrices.splice(index, 1);
            serviceDurations.splice(index, 1);
            serviceTelemedecines.splice(index, 1);
            serviceBookables.splice(index, 1);
            serviceVisitModes.splice(index, 1);
        });
        
        
        // Update state with cleaned arrays
        setAdditionalMultipleFields(prev => ({
            ...prev,
            services: services,
            service_price: servicePrices,
            service_duration: serviceDurations,
            service_telemedecine: serviceTelemedecines,
            service_bookable: serviceBookables,
            service_visit_mode: serviceVisitModes,
        }));
        
        // Also update formData to keep it in sync
        setFormData(prev => ({
            ...prev,
            services: services,
            service_price: servicePrices,
            service_duration: serviceDurations,
            service_telemedecine: serviceTelemedecines,
            service_bookable: serviceBookables,
            service_visit_mode: serviceVisitModes,
        }));
        
        // Check if this is for navigation or submission
        if (pendingNavigationCallback) {
            // Close dialog first
            showIncompleteServicesDialogRef.current = false;
            setShowIncompleteServicesDialog(false);
            setIncompleteServicesIndices([]);
            
            // Store callback and clear it from state
            const callback = pendingNavigationCallback;
            setPendingNavigationCallback(null);
            
            // Execute callback after state updates are processed
            // Use setTimeout to ensure React state updates are batched and processed
            setTimeout(() => {
                try {
                    if (typeof callback === 'function') {
                        callback();
                    } else {
                    }
                } catch (error) {
                    console.error('Error executing navigation callback:', error);
                }
            }, 0);
            return;
        }
        
        // This is for submission
        if (pendingSubmitData) {
            
            // Create data object with cleaned arrays (already removed above)
            const data = {
                ...pendingSubmitData,
                services: services,
                service_price: servicePrices,
                service_duration: serviceDurations,
                service_telemedecine: serviceTelemedecines,
                service_bookable: serviceBookables,
                service_visit_mode: serviceVisitModes,
            };
            
            // Close dialog and proceed with submission
            showIncompleteServicesDialogRef.current = false;
            setShowIncompleteServicesDialog(false);
            setPendingSubmitData(null);
            setIncompleteServicesIndices([]);
            
            // Proceed with submission
            setIsPublishing(true);
            proceedWithSubmission(data, pendingSubmitDoRest);
            return;
        }
        
        // If neither navigation nor submission is pending, just close the dialog
        // This handles cases where the dialog is shown but no action is pending
        showIncompleteServicesDialogRef.current = false;
        setShowIncompleteServicesDialog(false);
        setIncompleteServicesIndices([]);
        setPendingSubmitData(null);
        setPendingNavigationCallback(null);
        setIsPublishing(false);
    };

    // Handle completing incomplete services (scroll to first incomplete)
    const handleCompleteIncompleteServices = () => {
        
        // Store the indices before clearing state
        const indicesToScroll = [...incompleteServicesIndices];
        
        // Ensure incomplete services are preserved in form state
        // We need to ensure the services array has entries (even if empty) for incomplete services
        // so they don't get filtered out when ServiceForm re-initializes
        const currentServices = Array.isArray(additionalMultipleFields?.services) ? [...additionalMultipleFields.services] : [];
        const currentServicePrices = Array.isArray(additionalMultipleFields?.service_price) ? [...additionalMultipleFields.service_price] : [];
        const currentServiceDurations = Array.isArray(additionalMultipleFields?.service_duration) ? [...additionalMultipleFields.service_duration] : [];
        const currentServiceVisitModes = Array.isArray(additionalMultipleFields?.service_visit_mode) ? [...additionalMultipleFields.service_visit_mode] : [];
        
        // Ensure arrays are long enough to include incomplete service indices
        const maxLength = Math.max(
            currentServices.length,
            currentServicePrices.length,
            currentServiceDurations.length,
            currentServiceVisitModes.length,
            ...indicesToScroll.map(i => i + 1)
        );
        
        // Pad arrays with empty strings for incomplete services to preserve them
        for (let i = 0; i < maxLength; i++) {
            if (indicesToScroll.includes(i)) {
                // This is an incomplete service - ensure it has a placeholder in the services array
                if (!currentServices[i] || currentServices[i] === '') {
                    currentServices[i] = ''; // Empty string placeholder to preserve the index
                }
            }
        }
        
        // Update additionalMultipleFields to preserve incomplete services
        setAdditionalMultipleFields(prev => ({
            ...prev,
            services: currentServices,
            service_price: currentServicePrices,
            service_duration: currentServiceDurations,
            service_visit_mode: currentServiceVisitModes,
            form_section: 'services',
        }));
        
        
        // ALWAYS ensure we're on the services tab (step 2) - even if we're already there
        // This ensures we're on the right tab when user clicks "Complete Them"
        setValueSafe(1);
        setProviderProfileTabClass(providerTabClassDone);
        setProviderServiceTabClass(providerTabClassActive);
        setProviderAvailabilityTabClass(providerTabClass);
        
        // Close the dialog
        showIncompleteServicesDialogRef.current = false;
        setShowIncompleteServicesDialog(false);
        setPendingSubmitData(null);
        // Clear pending callback - user will need to click "Save draft" again after completing services
        setPendingNavigationCallback(null);
        setIncompleteServicesIndices([]);
        
        // Scroll to the first incomplete service after a brief delay to ensure tab switch completes
        if (indicesToScroll.length > 0) {
            const firstIncompleteIndex = indicesToScroll[0];
            setTimeout(() => {
                const serviceElement = document.getElementById(`service-${firstIncompleteIndex}`);
                if (serviceElement) {
                    serviceElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // Focus on the service select input
                    const selectInput = serviceElement.querySelector('select[name="service"]');
                    if (selectInput) {
                        setTimeout(() => {
                            selectInput.focus();
                        }, 300);
                    }
                }
            }, 200); // Slightly longer delay to ensure tab switch completes
        }
        
        setIsPublishing(false);
    };

    const removeAllEmptyStringValueAndArray = (obj) => {
        // List of keys that should be preserved even if empty (they're managed separately)
        const preserveKeys = ['services', 'service_price', 'service_duration', 'service_telemedecine', 'service_bookable'];
        
        Object.keys(obj).forEach((key) => {
            // Don't delete service arrays even if empty - they're needed for service management
            if (preserveKeys.includes(key)) {
                // Log if we're preserving a service key
                if (Array.isArray(obj[key])) {
                }
                return;
            }
            
            if (obj[key] === '' || (Array.isArray(obj[key]) && obj[key].length === 0 && !preserveKeys.includes(key))) {
                delete obj[key];
            }
        });

        return obj;
    };

    const createHandelString = (firstName, lastName) => {
        return firstName?.toLowerCase() + '-' + lastName?.toLowerCase();
    };

    const [disableService, setDisableService] = useState(true);
    const [disableAvailability, setDisableAvailability] = useState(true);
    const [additionalMultipleFields, setAdditionalMultipleFields] = useState({});
    const [consulationDetails, setConsulationDetails] = useState({});
    // Initialize consultationCheckboxes:
    // - Both add and edit mode start unchecked; edit mode updates when provider data loads
    const [consultationCheckboxes, setConsultationCheckboxes] = useState({ addInPerson: false, addTelemedicine: false });
    const [consultationPriceErrors, setConsultationPriceErrors] = useState({ inPerson: false, telemedicine: false });

    const [formData, setFormData] = useState({});

    const [value, setValue] = useState(0);

    const [initialFormState, setInitialFormState] = useState(null);
    const [initialStepState, setInitialStepState] = useState(null);
    const [formDataReady, setFormDataReady] = useState(false);
    const lastCapturedStepRef = useRef({ step: null, providerId: null });
    const formDataRef = useRef(formData);
    const additionalMultipleFieldsRef = useRef(additionalMultipleFields);
    const consulationDetailsRef = useRef(consulationDetails);
    
    // Helper function to check if changes require reapproval (synchronous version for button text)
    const checkIfChangesRequireReapproval = useMemo(() => {
        try {
            // Only check in edit mode when provider exists and has been submitted before (Active or rejected status)
            if (formState !== 'edit' || !selectedProvider || Object.keys(selectedProvider).length === 0) {
                return false;
            }

            // Only check if provider status is Active or rejected (has been submitted for review before)
            const status = (selectedProvider?.status || '').toLowerCase();
            if (status !== 'active' && status !== 'rejected') {
                return false;
            }

            const initial = selectedProvider;

            const normalizeStringArray = (arr) => {
                return (Array.isArray(arr) ? arr.map(v => (v ?? '').toString().trim()) : []);
            };
            const arraysEqualIgnoreOrder = (a, b) => {
                const aa = normalizeStringArray(a).filter(Boolean).sort();
                const bb = normalizeStringArray(b).filter(Boolean).sort();
                if (aa.length !== bb.length) return false;
                return aa.every((v, i) => v === bb[i]);
            };
            const tupleArray = (partsArrays) => {
                if (!Array.isArray(partsArrays)) {
                    return [];
                }
                const lengths = partsArrays.map(p => (Array.isArray(p) ? p.length : 0));
                const maxLen = lengths.length > 0 ? Math.max(...lengths) : 0;
                const tuples = [];
                for (let i = 0; i < maxLen; i++) {
                    const parts = partsArrays.map(p => (Array.isArray(p) ? (p[i] ?? '') : ''));
                    // Only include if there is at least one non-empty significant value
                    if (parts.some(v => (v ?? '').toString().trim() !== '')) {
                        tuples.push(parts.map(v => (v ?? '').toString().trim()).join('|'));
                    }
                }
                return tuples;
            };

            // Get current form data (merge formData and additionalMultipleFields)
            const currentData = { ...(formData || {}), ...(additionalMultipleFields || {}) };

            // Qualifications (titles)
            const currentQualifications = initial?.qualifications ?? initial?.qualification_title ?? [];
            const newQualifications = currentData?.qualification_title ?? [];
            const qualificationsChanged = !arraysEqualIgnoreOrder(currentQualifications, newQualifications);

            // Specialty (title + category) - use tuple comparison (synchronous)
            const currentSpecialityTitles = (initial?.specialties && initial?.specialties.length > 0)
                ? initial?.specialties
                : initial?.speciality;
            const currentCategories = initial?.categories;
            const newSpecialityTitles = currentData?.speciality;
            const newCategories = currentData?.categories;
            const currentSpecialtiesTuples = tupleArray([currentSpecialityTitles || [], currentCategories || []]).sort();
            const newSpecialtiesTuples = tupleArray([newSpecialityTitles || [], newCategories || []]).sort();
            const specialtiesChanged = (() => {
                if (currentSpecialtiesTuples.length !== newSpecialtiesTuples.length) return true;
                return !currentSpecialtiesTuples.every((v, i) => v === newSpecialtiesTuples[i]);
            })();

            // Licenses (number + state + year)
            const currentLicensesTuples = tupleArray([
                initial?.license_number || [],
                initial?.license_state || [],
                initial?.license_year || [],
            ]).sort();
            const newLicensesTuples = tupleArray([
                currentData?.license_number || [],
                currentData?.license_state || [],
                currentData?.license_year || [],
            ]).sort();
            const licensesChanged = (() => {
                if (currentLicensesTuples.length !== newLicensesTuples.length) return true;
                return !currentLicensesTuples.every((v, i) => v === newLicensesTuples[i]);
            })();

            // Board Certifications (cert + state + year)
            const currentCertsTuples = tupleArray([
                initial?.board_certification || [],
                initial?.board_certification_state || [],
                initial?.board_certification_year || [],
            ]).sort();
            const newCertsTuples = tupleArray([
                currentData?.board_certification || [],
                currentData?.board_certification_state || [],
                currentData?.board_certification_year || [],
            ]).sort();
            const certsChanged = (() => {
                if (currentCertsTuples.length !== newCertsTuples.length) return true;
                return !currentCertsTuples.every((v, i) => v === newCertsTuples[i]);
            })();

            // Education (degree + field + institution)
            const currentEducationTuples = tupleArray([
                initial?.education_degree || [],
                initial?.education_field || [],
                initial?.education_institute || [],
            ]).sort();
            const newEducationTuples = tupleArray([
                currentData?.education_degree || [],
                currentData?.education_field || [],
                currentData?.education_institute || [],
            ]).sort();
            const educationChanged = (() => {
                if (currentEducationTuples.length !== newEducationTuples.length) return true;
                return !currentEducationTuples.every((v, i) => v === newEducationTuples[i]);
            })();

            return qualificationsChanged || specialtiesChanged || licensesChanged || certsChanged || educationChanged;
        } catch(e) {
            console.error('Error in checkIfChangesRequireReapproval:', e);
            return false;
        }
    }, [formState, selectedProvider, formData, additionalMultipleFields]);

    // Same as checkIfChangesRequireReapproval but without status filter - used when saving from Availability tab
    // to decide whether to send 'In Review' (credentials changed) or preserve current status (no credential changes).
    const checkIfProfileCredentialsChanged = useMemo(() => {
        try {
            if (formState !== 'edit' || !selectedProvider || Object.keys(selectedProvider).length === 0) {
                return false;
            }
            const initial = selectedProvider;
            const normalizeStringArray = (arr) => (Array.isArray(arr) ? arr.map(v => (v ?? '').toString().trim()) : []);
            const arraysEqualIgnoreOrder = (a, b) => {
                const aa = normalizeStringArray(a).filter(Boolean).sort();
                const bb = normalizeStringArray(b).filter(Boolean).sort();
                if (aa.length !== bb.length) return false;
                return aa.every((v, i) => v === bb[i]);
            };
            const tupleArray = (partsArrays) => {
                if (!Array.isArray(partsArrays)) return [];
                const lengths = partsArrays.map(p => (Array.isArray(p) ? p.length : 0));
                const maxLen = lengths.length > 0 ? Math.max(...lengths) : 0;
                const tuples = [];
                for (let i = 0; i < maxLen; i++) {
                    const parts = partsArrays.map(p => (Array.isArray(p) ? (p[i] ?? '') : ''));
                    if (parts.some(v => (v ?? '').toString().trim() !== '')) {
                        tuples.push(parts.map(v => (v ?? '').toString().trim()).join('|'));
                    }
                }
                return tuples;
            };
            const currentData = { ...(formData || {}), ...(additionalMultipleFields || {}) };

            const currentQualifications = initial?.qualifications ?? initial?.qualification_title ?? [];
            const newQualifications = currentData?.qualification_title ?? [];
            if (!arraysEqualIgnoreOrder(currentQualifications, newQualifications)) return true;

            const currentSpecialityTitles = (initial?.specialties && initial?.specialties.length > 0) ? initial?.specialties : initial?.speciality;
            const currentCategories = initial?.categories;
            const newSpecialityTitles = currentData?.speciality;
            const newCategories = currentData?.categories;
            const currentSpecialtiesTuples = tupleArray([currentSpecialityTitles || [], currentCategories || []]).sort();
            const newSpecialtiesTuples = tupleArray([newSpecialityTitles || [], newCategories || []]).sort();
            if (currentSpecialtiesTuples.length !== newSpecialtiesTuples.length || !currentSpecialtiesTuples.every((v, i) => v === newSpecialtiesTuples[i])) return true;

            const currentLicensesTuples = tupleArray([initial?.license_number || [], initial?.license_state || [], initial?.license_year || []]).sort();
            const newLicensesTuples = tupleArray([currentData?.license_number || [], currentData?.license_state || [], currentData?.license_year || []]).sort();
            if (currentLicensesTuples.length !== newLicensesTuples.length || !currentLicensesTuples.every((v, i) => v === newLicensesTuples[i])) return true;

            const currentCertsTuples = tupleArray([initial?.board_certification || [], initial?.board_certification_state || [], initial?.board_certification_year || []]).sort();
            const newCertsTuples = tupleArray([currentData?.board_certification || [], currentData?.board_certification_state || [], currentData?.board_certification_year || []]).sort();
            if (currentCertsTuples.length !== newCertsTuples.length || !currentCertsTuples.every((v, i) => v === newCertsTuples[i])) return true;

            const currentEducationTuples = tupleArray([initial?.education_degree || [], initial?.education_field || [], initial?.education_institute || []]).sort();
            const newEducationTuples = tupleArray([currentData?.education_degree || [], currentData?.education_field || [], currentData?.education_institute || []]).sort();
            if (currentEducationTuples.length !== newEducationTuples.length || !currentEducationTuples.every((v, i) => v === newEducationTuples[i])) return true;

            return false;
        } catch (e) {
            console.error('Error in checkIfProfileCredentialsChanged:', e);
            return false;
        }
    }, [formState, selectedProvider, formData, additionalMultipleFields]);

    // Keep refs in sync with state
    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);
    
    useEffect(() => {
        additionalMultipleFieldsRef.current = additionalMultipleFields;
    }, [additionalMultipleFields]);
    
    useEffect(() => {
        consulationDetailsRef.current = consulationDetails;
    }, [consulationDetails]);

    const [openServicePopup, setOpenServicePopup] = useState(false);
    const [currentServiceIndex, setCurrentServiceIndex] = useState(null);
    const [serviceFormSyncRef, setServiceFormSyncRef] = useState(null);
    
    // State for incomplete services dialog
    const [showIncompleteServicesDialog, setShowIncompleteServicesDialog] = useState(false);
    const [incompleteServicesIndices, setIncompleteServicesIndices] = useState([]);
    const [pendingSubmitData, setPendingSubmitData] = useState(null);
    const [pendingSubmitDoRest, setPendingSubmitDoRest] = useState(null);
    const [pendingNavigationCallback, setPendingNavigationCallback] = useState(null);
    
    // Ref to track if we're showing incomplete services dialog (for synchronous access)
    const showIncompleteServicesDialogRef = useRef(false);
    
    // Wrapper for setValue to prevent navigation when dialog is open
    const setValueSafe = (newValue) => {
        
        // Prevent navigation to step 2 or 3 if dialog is open
        if (showIncompleteServicesDialog && (newValue === 2 || newValue === 3)) {
            return;
        }
        
        setValue(newValue);
    };

    // Track dialog state changes
    useEffect(() => {
    }, [showIncompleteServicesDialog, incompleteServicesIndices]);

    const baseProviderTabClass = 'provider-tab text-sm font-medium transition-colors duration-200';
    const providerTabClass = `${baseProviderTabClass} text-gray-500`;
    const providerTabClassActive = `${baseProviderTabClass} text-[#184353]`;
    const providerTabClassDone = `${baseProviderTabClass} text-[#75BD2F]`;

    const [providerProfileTabClass, setProviderProfileTabClass] = useState(providerTabClassActive);
    const [providerServiceTabClass, setProviderServiceTabClass] = useState(providerTabClass);
    const [providerAvailabilityTabClass, setProviderAvailabilityTabClass] = useState(providerTabClass);

    const ProfileLabel = () => <span className={providerProfileTabClass}>Profile</span>;
    const ServiceLabel = () => <span className={providerServiceTabClass}>Services</span>;
    const AvailabilityLabel = () => <span className={providerAvailabilityTabClass}>Availability</span>;

    // Function to get current step data for comparison.
    // Read directly from state (not refs) so the comparison is synchronous with the
    // latest render. Refs are updated in effects that run AFTER render, so reading
    // them here lags one update behind and makes single-field edits (e.g. changing
    // only the availability location) go undetected by hasStepChanges.
    const getCurrentStepData = useCallback(() => {
        const currentFormData = formData || {};
        const currentAdditionalFields = additionalMultipleFields || {};
        const currentConsulationDetails = consulationDetails || {};

        if (value === 0) {
            // Profile step
            return {
                formData: { ...currentFormData },
                additionalMultipleFields: { ...currentAdditionalFields }
            };
        } else if (value === 1) {
            // Services step
            // Return consulationDetails as-is for comparison (don't inject defaults)
            return {
                formData: { ...currentFormData },
                additionalMultipleFields: { ...currentAdditionalFields },
                consulationDetails: { ...currentConsulationDetails }
            };
        } else if (value === 2) {
            // Availability step
            return {
                formData: { ...currentFormData },
                additionalMultipleFields: { ...currentAdditionalFields }
            };
        }
        return null;
    }, [value, formData, additionalMultipleFields, consulationDetails]);

    // Always show the Availability tab alongside Profile and Services.
    const showAvailabilityTab = true;

    // Capture a stable baseline after provider data loads
    useEffect(() => {
        if (selectedProvider && Object.keys(selectedProvider).length > 0) {
            setInitialFormState(JSON.stringify(selectedProvider));
        }
    }, [selectedProvider]);

    const handleTabChange = (event, newValue) => {
        
        // Prevent navigation if dialog is open
        if (showIncompleteServicesDialog) {
            return;
        }
        
        // Check for incomplete services before navigating away from services tab
        checkAndHandleIncompleteServices(() => {
            // When switching tabs, persist form section data
            if (value === 1 && newValue !== 1) {
                // Save services data before leaving Services tab
                setAdditionalMultipleFields({
                    ...additionalMultipleFields,
                    form_section: 'services',
                    services: formData.services,
                    service_price: formData.service_price,
                    service_duration: formData.service_duration,
                    service_telemedecine: formData.service_telemedecine,
                    service_bookable: formData.service_bookable,
                });
            }

            // Prevent navigating to Availability if it's hidden
            if (newValue === 2 && !showAvailabilityTab) {
                setValueSafe(1);
                setProviderProfileTabClass(providerTabClassDone);
                setProviderServiceTabClass(providerTabClassActive);
                setProviderAvailabilityTabClass(providerTabClass);
                return;
            }

            setValueSafe(newValue);
            setFormState('edit');
            
            // Update tab classes based on the selected tab
            if (newValue === 0) {
                setProviderProfileTabClass(providerTabClassActive);
                setProviderServiceTabClass(providerTabClass);
                setProviderAvailabilityTabClass(providerTabClass);
            } else if (newValue === 1) {
                setProviderProfileTabClass(providerTabClassDone);
                setProviderServiceTabClass(providerTabClassActive);
                setProviderAvailabilityTabClass(providerTabClass);
            } else if (newValue === 2) {
                setProviderProfileTabClass(providerTabClassDone);
                setProviderServiceTabClass(providerTabClassDone);
                setProviderAvailabilityTabClass(providerTabClassActive);
            }
        });
    };

    // Check if current step has changes
    // This recomputes immediately whenever formData, additionalMultipleFields, or consulationDetails change
    const hasStepChanges = useMemo(() => {
        // If initial state hasn't been captured yet, no changes detected
        if (!initialStepState) return false;
        
        const currentStepData = getCurrentStepData();
        if (!currentStepData) return false;
        
        // Deep comparison using JSON stringify
        // Normalize empty objects and arrays to avoid false positives
        const normalizedCurrent = JSON.stringify(currentStepData);
        const normalizedInitial = JSON.stringify(initialStepState);
        
        return normalizedCurrent !== normalizedInitial;
    }, [initialStepState, getCurrentStepData, value, formData, additionalMultipleFields, consulationDetails, formState]);

    // Determine if there are any unsaved changes across the form
    const hasUnsavedChanges = useMemo(() => {
        if (hasStepChanges) return true;
        if (initialFormState != null) {
            return JSON.stringify(formData) !== initialFormState;
        }
        // For add mode, check if there's actual user-entered data (not just default/empty values)
        const formDataKeys = formData ? Object.keys(formData) : [];
        const additionalFieldsKeys = additionalMultipleFields ? Object.keys(additionalMultipleFields) : [];
        const consultationDetailsKeys = consulationDetails ? Object.keys(consulationDetails) : [];
        
        // Check formData - only count if there are meaningful user-entered values
        // Exclude default/programmatic fields like 'gender' which may have default values
        const excludeFormDataKeys = ['gender']; // Fields that may have defaults but aren't user input
        const meaningfulFormDataKeys = formDataKeys.filter(key => !excludeFormDataKeys.includes(key));
        const hasFormData = meaningfulFormDataKeys.length > 0 && 
            meaningfulFormDataKeys.some(key => {
                const val = formData[key];
                if (Array.isArray(val)) {
                    return val.length > 0 && val.some(v => v && String(v).trim() !== '' && v !== '0');
                }
                if (typeof val === 'object' && val !== null) {
                    return Object.keys(val).length > 0;
                }
                return val !== '' && val !== null && val !== undefined && val !== 0;
            });
        
        // Check additionalMultipleFields - only count if there are meaningful user-entered values
        // Exclude programmatic fields like 'form_section' which is set automatically
        const excludeAdditionalFieldsKeys = ['form_section']; // Fields set programmatically, not by user
        const meaningfulAdditionalFieldsKeys = additionalFieldsKeys.filter(key => !excludeAdditionalFieldsKeys.includes(key));
        const hasAdditionalFields = meaningfulAdditionalFieldsKeys.length > 0 &&
            meaningfulAdditionalFieldsKeys.some(key => {
                const val = additionalMultipleFields[key];
                if (Array.isArray(val)) {
                    return val.length > 0 && val.some(v => v && String(v).trim() !== '' && v !== '0');
                }
                if (typeof val === 'object' && val !== null) {
                    return Object.keys(val).length > 0;
                }
                return val !== '' && val !== null && val !== undefined && val !== 0;
            });
        
        // For consulationDetails, only count as changes if there's actual price data or URL
        // (duration defaults to 15 when checkbox is checked, so we ignore that)
        const hasConsultationDetails = consultationDetailsKeys.length > 0 &&
            (consulationDetails?.in_person_consultation_price || 
             consulationDetails?.telemedicine_consultation_price ||
             consulationDetails?.telemedicine_url);
        
        return hasFormData || hasAdditionalFields || hasConsultationDetails;
    }, [hasStepChanges, initialFormState, formData, additionalMultipleFields, consulationDetails, formState]);

    // Actual save logic extracted to separate function to avoid recursion
    const executeDraftSave = async () => {
        try {
            setIsPublishing(true);
            
            // Determine form_section based on current step
            let formSection = 'profile';
            if (value === 1) formSection = 'services';
            if (value === 2) formSection = 'availability';
            
            // Merge form data
            let data = removeAllEmptyStringValueAndArray({ ...formData, ...additionalMultipleFields });
            
            // Ensure services data is included if on services step
            if (formSection === 'services') {
                // Merge consultation details
                const finalConsultationDetails = {
                    in_person_consultation_duration: consulationDetails?.in_person_consultation_duration !== undefined ? consulationDetails.in_person_consultation_duration : (data?.in_person_consultation_duration || formData?.in_person_consultation_duration || 0),
                    in_person_consultation_price: consulationDetails?.in_person_consultation_price !== undefined ? consulationDetails.in_person_consultation_price : (data?.in_person_consultation_price || formData?.in_person_consultation_price || 0),
                    telemedicine_consultation_duration: consulationDetails?.telemedicine_consultation_duration !== undefined ? consulationDetails.telemedicine_consultation_duration : (data?.telemedicine_consultation_duration || formData?.telemedicine_consultation_duration || 0),
                    telemedicine_consultation_price: consulationDetails?.telemedicine_consultation_price !== undefined ? consulationDetails.telemedicine_consultation_price : (data?.telemedicine_consultation_price || formData?.telemedicine_consultation_price || 0),
                    telemedicine_url: consulationDetails?.telemedicine_url || data?.telemedicine_url || formData?.telemedicine_url || '',
                    exclude_FMP_inperson: consulationDetails?.exclude_FMP_inperson === true || data?.exclude_FMP_inperson === true || formData?.exclude_FMP_inperson === true,
                    exclude_FMP_telemedicine: consulationDetails?.exclude_FMP_telemedicine === true || data?.exclude_FMP_telemedicine === true || formData?.exclude_FMP_telemedicine === true,
                };
                
                data = { 
                    ...data,
                    ...finalConsultationDetails
                };
            }
            
            data['status'] = 'draft';
            data['form_section'] = formSection;

            if (formSection === 'profile') {
                if (formState === 'edit' && selectedProvider?.id) {
                    data['status'] = 'draft';
                    await updateProvider( data, 'profile', false);
                } else {
                    await createProvider(data);
                }
            } else {
                if (!selectedProvider?.id && !providerId) {
                    setIsPublishing(false);
                    return;
                }
                
                if (formSection === 'services') {
                    data['status'] = 'draft';
                    // Save complete services to provider_services table (updateServiceSection filters incomplete ones)
                    await updateProvider( data, 'services', false);
                } else if (formSection === 'availability') {
                    data['status'] = 'draft';
                    await updateProvider( data, 'availability', false);
                }
            }
            
            resetForm();
            onClose();
        } catch (error) {
            console.error('Error saving draft:', error);
            toasterNotification('Error saving draft. Please try again.', 'error');
        } finally {
            setIsPublishing(false);
        }
    };
    
    // Handle save draft
    const handleSaveDraft = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        // Prevent multiple simultaneous calls
        if (showIncompleteServicesDialog || showIncompleteServicesDialogRef.current) {
            return;
        }
        
        
        // Check for incomplete services if on services tab (same as navigation check)
        if (value === 1) {
            const services = Array.isArray(additionalMultipleFields?.services) 
                ? additionalMultipleFields.services 
                : (Array.isArray(formData?.services) ? formData.services : []);
            const servicePrices = Array.isArray(additionalMultipleFields?.service_price) 
                ? additionalMultipleFields.service_price 
                : (Array.isArray(formData?.service_price) ? formData.service_price : []);
            const serviceDurations = Array.isArray(additionalMultipleFields?.service_duration) 
                ? additionalMultipleFields.service_duration 
                : (Array.isArray(formData?.service_duration) ? formData.service_duration : []);
            const serviceVisitModes = Array.isArray(additionalMultipleFields?.service_visit_mode) 
                ? additionalMultipleFields.service_visit_mode 
                : (Array.isArray(formData?.service_visit_mode) ? formData.service_visit_mode : []);
            
            const incompleteIndices = checkForIncompleteServices(services, servicePrices, serviceDurations, serviceVisitModes);
            
            
            if (incompleteIndices.length > 0) {
                // Show dialog and store the draft save action as pending callback
                setIncompleteServicesIndices(incompleteIndices);
                // Store callback that will execute the save logic directly (not via handleSaveDraft to avoid recursion)
                setPendingNavigationCallback(() => {
                    // This callback will be executed after user removes incomplete services
                    executeDraftSave();
                });
                showIncompleteServicesDialogRef.current = true;
                setShowIncompleteServicesDialog(true);
                return; // Don't proceed with save until user resolves incomplete services
            }
        }
        
        // No incomplete services, proceed with save
        executeDraftSave();
    };

    // Function to validate if required fields are filled based on current tab
    const isFormValid = useMemo(() => {
        // Include consultationCheckboxes in dependencies so validation updates when checkboxes change
        if (value === 0) {
            // Profile tab validation
            // Check basic required fields
            const basicFieldsValid = (
                !!formData?.first_name?.trim() &&
                !!formData?.last_name?.trim() &&
                !!formData?.email?.trim() &&
                !!formData?.image_url &&
                !!formData?.gender
            );

            // In edit mode, allow progressing to Services when core basics are complete.
            // Some legacy providers may be missing optional profile details that shouldn't block navigation.
            if (formState === 'edit') {
                return basicFieldsValid;
            }
            
            // Check qualification title - at least one must be provided
            const qualificationTitles = Array.isArray(formData?.qualification_title) ? formData.qualification_title : 
                                     Array.isArray(additionalMultipleFields?.qualification_title) ? additionalMultipleFields.qualification_title : [];
            let qualificationValid = false;
            
            if (qualificationTitles.length > 0) {
                for (let i = 0; i < qualificationTitles.length; i++) {
                    if (qualificationTitles[i] && typeof qualificationTitles[i] === 'string' && qualificationTitles[i].trim() !== '') {
                        qualificationValid = true;
                        break;
                    }
                }
            }
            
            // Check specialty fields - at least one specialty category must be selected
            let specialtyValid = false;
            const categories = Array.isArray(formData?.categories) ? formData.categories : 
                              Array.isArray(additionalMultipleFields?.categories) ? additionalMultipleFields.categories : [];
            
            if (categories.length > 0) {
                for (let i = 0; i < categories.length; i++) {
                    if (categories[i]) {
                        specialtyValid = true;
                        break;
                    }
                }
            }
            
            // Check license fields - at least one license must be provided
            let licenseValid = false;
            const licenseNumbers = Array.isArray(formData?.license_number) ? formData.license_number : 
                                 Array.isArray(additionalMultipleFields?.license_number) ? additionalMultipleFields.license_number : [];
            const licenseStates = Array.isArray(formData?.license_state) ? formData.license_state : 
                                 Array.isArray(additionalMultipleFields?.license_state) ? additionalMultipleFields.license_state : [];
            
            if (licenseNumbers.length > 0 && licenseStates.length > 0) {
                for (let i = 0; i < licenseNumbers.length; i++) {
                    if (licenseNumbers[i] && typeof licenseNumbers[i] === 'string' && licenseNumbers[i].trim() !== '' && 
                        licenseStates[i] && typeof licenseStates[i] === 'string' && licenseStates[i].trim() !== '') {
                        licenseValid = true;
                        break;
                    }
                }
            }
            
            // Check board certification fields - at least one certification must be provided
            let boardCertValid = false;
            const boardCertifications = Array.isArray(formData?.board_certification) ? formData.board_certification : 
                                       Array.isArray(additionalMultipleFields?.board_certification) ? additionalMultipleFields.board_certification : [];
            const boardCertStates = Array.isArray(formData?.board_certification_state) ? formData.board_certification_state : 
                                    Array.isArray(additionalMultipleFields?.board_certification_state) ? additionalMultipleFields.board_certification_state : [];
            
            if (boardCertifications.length > 0 && boardCertStates.length > 0) {
                for (let i = 0; i < boardCertifications.length; i++) {
                    if (boardCertifications[i] && typeof boardCertifications[i] === 'string' && boardCertifications[i].trim() !== '' && 
                        boardCertStates[i] && typeof boardCertStates[i] === 'string' && boardCertStates[i].trim() !== '') {
                        boardCertValid = true;
                        break;
                    }
                }
            }
            
            return basicFieldsValid && qualificationValid && specialtyValid && licenseValid && boardCertValid;
        } else if (value === 1) {
            // Services tab validation - at least one service option must be selected
            
            // Prioritize current form state over old data
            const servicesInFormData = Array.isArray(formData?.services) ? formData.services : [];
            const servicesInAdditional = Array.isArray(additionalMultipleFields?.services) ? additionalMultipleFields.services : [];
            
            // Use additionalMultipleFields as source of truth if it exists, otherwise use formData
            const currentServices = servicesInAdditional.length > 0 ? servicesInAdditional : servicesInFormData;
            
            // More robust service validation to catch placeholder values
            const hasServices = currentServices.length > 0 && currentServices.some(service => {
                // Must exist and not be falsy
                if (!service) return false;
                
                // Convert to string and check for various invalid values
                const serviceStr = String(service).trim();
                
                // Empty string
                if (serviceStr === '') return false;
                
                // Common placeholder values
                if (serviceStr === '0' || serviceStr === 0) return false;
                if (serviceStr === '-1' || serviceStr === -1) return false;
                if (serviceStr === 'null' || serviceStr === 'undefined') return false;
                
                // Placeholder text patterns
                const lowerStr = serviceStr.toLowerCase();
                if (lowerStr.includes('select')) return false;
                if (lowerStr.includes('choose')) return false;
                if (lowerStr.includes('pick')) return false;
                
                // If it's a valid positive number, it's probably a service ID
                if (!isNaN(serviceStr) && parseFloat(serviceStr) > 0) return true;
                
                // If it's a non-empty string that doesn't look like a placeholder, accept it
                return serviceStr.length > 0;
            });
            
            const hasInPersonDuration = !!consulationDetails?.in_person_consultation_duration && 
                                       consulationDetails.in_person_consultation_duration !== 0;
            // Treat '0.00' or empty strings as no price (allow user to type without validation failing)
            const inPersonPriceValue = consulationDetails?.in_person_consultation_price;
            const hasInPersonPrice = !!inPersonPriceValue && 
                                    inPersonPriceValue !== '' && 
                                    inPersonPriceValue !== '0' && 
                                    inPersonPriceValue !== '0.00' &&
                                    inPersonPriceValue !== 0;
            const hasInPersonConsultation = hasInPersonDuration && hasInPersonPrice;
            
            const hasTelemedicineDuration = !!consulationDetails?.telemedicine_consultation_duration && 
                                           consulationDetails.telemedicine_consultation_duration !== 0;
            // Treat '0.00' or empty strings as no price (allow user to type without validation failing)
            const telemedicinePriceValue = consulationDetails?.telemedicine_consultation_price;
            const hasTelemedicinePrice = !!telemedicinePriceValue && 
                                        telemedicinePriceValue !== '' && 
                                        telemedicinePriceValue !== '0' && 
                                        telemedicinePriceValue !== '0.00' &&
                                        telemedicinePriceValue !== 0;
            const hasTelemedicineConsultation = hasTelemedicineDuration && hasTelemedicinePrice;
            
            // At least one service option must be selected
            const hasAtLeastOneOption = hasServices || hasInPersonConsultation || hasTelemedicineConsultation;

            // If at least one individual service is selected, allow proceeding regardless of consultation checkbox states
            if (hasServices) {
                return true;
            }
            
            // For edit mode, be more lenient about partial data but still require at least one option
            if (formState === 'edit' && selectedProvider) {
                return hasAtLeastOneOption;
            }
            
            // For new providers, enforce strict validation
            // If in-person checkbox is checked, require both duration AND price
            if (consultationCheckboxes.addInPerson) {
                if (!hasInPersonConsultation) return false;
            } else {
                // If checkbox is unchecked, but user started filling fields, both must be complete
                if (hasInPersonDuration || hasInPersonPrice) {
                    if (!hasInPersonConsultation) return false;
                }
            }
            
            // If telemedicine checkbox is checked, require both duration AND price (or URL-only)
            const hasTelemedicineUrl = !!consulationDetails?.telemedicine_url && consulationDetails.telemedicine_url.length > 0;
            if (consultationCheckboxes.addTelemedicine) {
                // Checkbox is checked - require complete consultation OR URL-only
                if (hasTelemedicineUrl) {
                    // URL-only telemedicine is valid when checkbox is checked
                    return true;
                } else {
                    // Require both duration and price when checkbox is checked
                    if (!hasTelemedicineConsultation) return false;
                }
            } else {
                // Checkbox is unchecked, but if user started filling fields, both must be complete
                if (hasTelemedicineDuration || hasTelemedicinePrice) {
                    if (!hasTelemedicineConsultation) return false;
                }
            }
            
            // Allow proceeding with only individual services (no consultation types required)
            return hasAtLeastOneOption;
        } else if (value === 2) {
            // Availability tab validation
            try {
                // Determine if this provider offers any in-person consultation.
                // When "In-person" is enabled in Services, at least one real
                // location must be configured for in-person availability.
                // Location remains optional only for "Telemedicine only".
                const requiresInPersonLocation = !!(consultationCheckboxes?.addInPerson);

                // If we have no availability container at all, treat as "no data"
                if (!additionalMultipleFields || !additionalMultipleFields.availability_day) {
                    // No availability is allowed only for telemedicine‑only providers
                    return requiresInPersonLocation ? false : true;
                }

                // Get availability arrays with safety checks
                const days = additionalMultipleFields.availability_day || [];
                const startTimes = additionalMultipleFields.availability_start_time || [];
                const endTimes = additionalMultipleFields.availability_end_time || [];
                const locations = additionalMultipleFields.availability_location || [];
                const telemedicineFlags = additionalMultipleFields.telemedicine || [];

                // If no availability rows defined:
                // - valid for telemedicine‑only
                // - NOT valid when in‑person consultations are offered
                if (days.length === 0) {
                    return requiresInPersonLocation ? false : true;
                }

                let hasValidInPersonSlotWithLocation = false;
                let hasValidTelemedicineSlot = false;

                // If availability data is provided, validate only the filled entries
                for (let i = 0; i < days.length; i++) {
                    // Only validate if at least one field in this entry is filled
                    const hasAnyData = days[i] || startTimes[i] || endTimes[i] || locations[i];

                    if (!hasAnyData) {
                        continue;
                    }

                    const isTelemedicine = telemedicineFlags[i] === true || telemedicineFlags[i] === '1';
                    const locationValue = locations[i];

                    // Basic required fields for all entries
                    if (
                        !days[i] || days[i] === '' ||
                        !startTimes[i] || startTimes[i] === '' ||
                        !endTimes[i] || endTimes[i] === ''
                    ) {
                        return false;
                    }

                    // Track valid telemedicine slots (day + time filled; location optional)
                    if (isTelemedicine) {
                        hasValidTelemedicineSlot = true;
                    }

                    // In‑person-only rows require a real location; invalid if missing.
                    if (requiresInPersonLocation) {
                        if (!isTelemedicine) {
                            if (!locationValue || locationValue === '' || locationValue === '0') {
                                // In‑person slot without a location -> invalid
                                return false;
                            }
                            hasValidInPersonSlotWithLocation = true;
                        } else if (locationValue && locationValue !== '' && locationValue !== '0') {
                            hasValidInPersonSlotWithLocation = true;
                        }
                    }
                }

                // For providers offering in‑person only: require at least one in‑person slot with location.
                // For providers offering both in‑person and telemedicine: require at least one in‑person
                // slot with location OR at least one valid telemedicine slot (so "Telemedicine only" or
                // "In-Person & Telemedicine" rows satisfy validation).
                const offersBoth = !!(consultationCheckboxes?.addInPerson && consultationCheckboxes?.addTelemedicine);
                if (requiresInPersonLocation && !hasValidInPersonSlotWithLocation) {
                    if (offersBoth && hasValidTelemedicineSlot) {
                        // Allow submit with only telemedicine/both slots when provider offers both
                    } else {
                        return false;
                    }
                }

                // All validation passed
                return true;
            } catch (error) {
                console.error("Error in availability validation:", error);
                // In case of unexpected error, fail closed when in‑person is required
                return consultationCheckboxes?.addInPerson ? false : true;
            }
        }
        
        return false;
    }, [value, formData, additionalMultipleFields, consulationDetails, formState, selectedProvider, consultationCheckboxes]);

    // Function to check if a specific step is completed
    const isStepCompleted = (stepIndex) => {
        if (stepIndex === 0) {
            // Profile step validation - same as isFormValid for step 0
            const basicFieldsValid = (
                !!formData?.first_name?.trim() &&
                !!formData?.last_name?.trim() &&
                !!formData?.email?.trim() &&
                !!formData?.image_url &&
                !!formData?.gender
            );

            // In edit mode, treat the step as completed when core basics are filled
            if (formState === 'edit') {
                return basicFieldsValid;
            }
            
            const qualificationTitles = Array.isArray(formData?.qualification_title) ? formData.qualification_title : 
                                     Array.isArray(additionalMultipleFields?.qualification_title) ? additionalMultipleFields.qualification_title : [];
            let qualificationValid = false;
            
            if (qualificationTitles.length > 0) {
                for (let i = 0; i < qualificationTitles.length; i++) {
                    if (qualificationTitles[i] && typeof qualificationTitles[i] === 'string' && qualificationTitles[i].trim() !== '') {
                        qualificationValid = true;
                        break;
                    }
                }
            }
            
            let specialtyValid = false;
            const categories = Array.isArray(formData?.categories) ? formData.categories : 
                              Array.isArray(additionalMultipleFields?.categories) ? additionalMultipleFields.categories : [];
            
            if (categories.length > 0) {
                for (let i = 0; i < categories.length; i++) {
                    if (categories[i]) {
                        specialtyValid = true;
                        break;
                    }
                }
            }
            
            let licenseValid = false;
            const licenseNumbers = Array.isArray(formData?.license_number) ? formData.license_number : 
                                 Array.isArray(additionalMultipleFields?.license_number) ? additionalMultipleFields.license_number : [];
            const licenseStates = Array.isArray(formData?.license_state) ? formData.license_state : 
                                 Array.isArray(additionalMultipleFields?.license_state) ? additionalMultipleFields.license_state : [];
            
            if (licenseNumbers.length > 0 && licenseStates.length > 0) {
                for (let i = 0; i < licenseNumbers.length; i++) {
                    if (licenseNumbers[i] && typeof licenseNumbers[i] === 'string' && licenseNumbers[i].trim() !== '' && 
                        licenseStates[i] && typeof licenseStates[i] === 'string' && licenseStates[i].trim() !== '') {
                        licenseValid = true;
                        break;
                    }
                }
            }
            
            let boardCertValid = false;
            const boardCertifications = Array.isArray(formData?.board_certification) ? formData.board_certification : 
                                       Array.isArray(additionalMultipleFields?.board_certification) ? additionalMultipleFields.board_certification : [];
            const boardCertStates = Array.isArray(formData?.board_certification_state) ? formData.board_certification_state : 
                                    Array.isArray(additionalMultipleFields?.board_certification_state) ? additionalMultipleFields.board_certification_state : [];
            
            if (boardCertifications.length > 0 && boardCertStates.length > 0) {
                for (let i = 0; i < boardCertifications.length; i++) {
                    if (boardCertifications[i] && typeof boardCertifications[i] === 'string' && boardCertifications[i].trim() !== '' && 
                        boardCertStates[i] && typeof boardCertStates[i] === 'string' && boardCertStates[i].trim() !== '') {
                        boardCertValid = true;
                        break;
                    }
                }
            }
            
            return basicFieldsValid && qualificationValid && specialtyValid && licenseValid && boardCertValid;
        } else if (stepIndex === 1) {
            // Services step validation - same as isFormValid for step 1
            const servicesInFormData = Array.isArray(formData?.services) ? formData.services : [];
            const servicesInAdditional = Array.isArray(additionalMultipleFields?.services) ? additionalMultipleFields.services : [];
            
            const currentServices = servicesInAdditional.length > 0 ? servicesInAdditional : servicesInFormData;
            
            const hasServices = currentServices.length > 0 && currentServices.some(service => {
                if (!service) return false;
                
                const serviceStr = String(service).trim();
                
                if (serviceStr === '') return false;
                if (serviceStr === '0' || serviceStr === 0) return false;
                if (serviceStr === '-1' || serviceStr === -1) return false;
                if (serviceStr === 'null' || serviceStr === 'undefined') return false;
                
                const lowerStr = serviceStr.toLowerCase();
                if (lowerStr.includes('select')) return false;
                if (lowerStr.includes('choose')) return false;
                if (lowerStr.includes('pick')) return false;
                
                if (!isNaN(serviceStr) && parseFloat(serviceStr) > 0) return true;
                
                return serviceStr.length > 0;
            });
            
            const hasInPersonDuration = !!consulationDetails?.in_person_consultation_duration && 
                                       consulationDetails.in_person_consultation_duration !== 0;
            // Treat '0.00' or empty strings as no price (allow user to type without validation failing)
            const inPersonPriceValue = consulationDetails?.in_person_consultation_price;
            const hasInPersonPrice = !!inPersonPriceValue && 
                                    inPersonPriceValue !== '' && 
                                    inPersonPriceValue !== '0' && 
                                    inPersonPriceValue !== '0.00' &&
                                    inPersonPriceValue !== 0;
            const hasInPersonConsultation = hasInPersonDuration && hasInPersonPrice;
            
            const hasTelemedicineDuration = !!consulationDetails?.telemedicine_consultation_duration && 
                                           consulationDetails.telemedicine_consultation_duration !== 0;
            // Treat '0.00' or empty strings as no price (allow user to type without validation failing)
            const telemedicinePriceValue = consulationDetails?.telemedicine_consultation_price;
            const hasTelemedicinePrice = !!telemedicinePriceValue && 
                                        telemedicinePriceValue !== '' && 
                                        telemedicinePriceValue !== '0' && 
                                        telemedicinePriceValue !== '0.00' &&
                                        telemedicinePriceValue !== 0;
            const hasTelemedicineConsultation = hasTelemedicineDuration && hasTelemedicinePrice;
            
            const hasAtLeastOneOption = hasServices || hasInPersonConsultation || hasTelemedicineConsultation;

            // If at least one individual service is selected, consider the step completed
            if (hasServices) {
                return true;
            }
            
            // For edit mode, just check if at least one option exists
            if (formState === 'edit' && selectedProvider) {
                return hasAtLeastOneOption;
            }
            
            // For add mode, validate more strictly
            // If in-person checkbox is checked, require both duration AND price
            if (consultationCheckboxes.addInPerson) {
                if (!hasInPersonConsultation) return false;
            } else {
                // If checkbox is unchecked, but user started filling fields, both must be complete
                if (hasInPersonDuration || hasInPersonPrice) {
                    if (!hasInPersonConsultation) return false;
                }
            }
            
            // If telemedicine checkbox is checked, require both duration AND price (or URL-only)
            const hasTelemedicineUrl = !!consulationDetails?.telemedicine_url && consulationDetails.telemedicine_url.length > 0;
            if (consultationCheckboxes.addTelemedicine) {
                // Checkbox is checked - require complete consultation OR URL-only
                if (hasTelemedicineUrl) {
                    // URL-only telemedicine is valid when checkbox is checked
                    return true;
                } else {
                    // Require both duration and price when checkbox is checked
                    if (!hasTelemedicineConsultation) return false;
                }
            } else {
                // Checkbox is unchecked, but if user started filling fields, both must be complete
                if (hasTelemedicineDuration || hasTelemedicinePrice) {
                    if (!hasTelemedicineConsultation) return false;
                }
            }
            
            // Allow proceeding with only individual services (no consultation types required)
            return hasAtLeastOneOption;
        } else if (stepIndex === 2) {
            // Availability step validation - always returns true since availability is optional
            return true;
        }
        
        return false;
    };

    useEffect(() => {
        if (open) {
            // Don't set initial form state here - wait for selectedProvider to load
            setFormDataReady(false);
            setInitialStepState(null);
            lastCapturedStepRef.current = { step: null, providerId: null };
        }
    }, [open]);

    // Capture initial step state when form opens or data is ready
    useEffect(() => {
        if (!open) return;
        
        // For add mode
        // Check if selectedProvider is null/undefined OR empty object (add mode uses {} as placeholder)
        const isAddMode = formState === 'add' && (!selectedProvider || Object.keys(selectedProvider).length === 0);
        if (isAddMode) {
            const currentStep = value;
            
            // Always capture if we haven't captured for this step yet
            // This ensures we capture when form opens (step is null) OR when switching tabs
            // Capture if step changed OR if this is first load (step is null)
            const stepChanged = lastCapturedStepRef.current.step !== currentStep;
            const isFirstLoad = lastCapturedStepRef.current.step === null;
            
            if (stepChanged || isFirstLoad) {
                // For add mode, we need to wait for formData to stabilize
                // The useEffect at line 1698 might modify formData, so we use a longer delay
                // or wait for formData to stop changing. Use requestAnimationFrame for better timing.
                const delay = value === 1 ? 200 : 150;
                const timer = setTimeout(() => {
                    // Access latest values via refs to get current state (same as edit mode)
                    let currentStepData = null;
                    const currentFormData = formDataRef.current || {};
                    const currentAdditionalFields = additionalMultipleFieldsRef.current || {};
                    const currentConsulationDetails = consulationDetailsRef.current || {};
                    
                    if (value === 0) {
                        currentStepData = {
                            formData: { ...currentFormData },
                            additionalMultipleFields: { ...currentAdditionalFields }
                        };
                    } else if (value === 1) {
                        currentStepData = {
                            formData: { ...currentFormData },
                            additionalMultipleFields: { ...currentAdditionalFields },
                            consulationDetails: { ...currentConsulationDetails }
                        };
                    } else if (value === 2) {
                        currentStepData = {
                            formData: { ...currentFormData },
                            additionalMultipleFields: { ...currentAdditionalFields }
                        };
                    }
                    
                    // In add mode, always capture even if data is empty (empty is the initial state)
                    if (currentStepData) {
                        setInitialStepState(currentStepData);
                        lastCapturedStepRef.current = { step: currentStep, providerId: null };
                        setFormDataReady(true);
                    }
                }, delay);
                return () => clearTimeout(timer);
            }
        }
        
        // For edit mode
        if (formState === 'edit' && selectedProvider?.id) {
            const currentProviderId = selectedProvider.id;
            const currentStep = value;
            
            // Only capture if we haven't captured for this step/provider combo yet
            if (lastCapturedStepRef.current.step !== currentStep || 
                lastCapturedStepRef.current.providerId !== currentProviderId) {
                
                // Delay longer for services step to allow ServiceForm to initialize
                const delay = value === 1 ? 200 : 100;
                const timer = setTimeout(() => {
                    // Access latest values via refs to get current state
                    let currentStepData = null;
                    const currentFormData = formDataRef.current || {};
                    const currentAdditionalFields = additionalMultipleFieldsRef.current || {};
                    const currentConsulationDetails = consulationDetailsRef.current || {};
                    
                    if (value === 0) {
                        currentStepData = {
                            formData: { ...currentFormData },
                            additionalMultipleFields: { ...currentAdditionalFields }
                        };
                    } else if (value === 1) {
                        currentStepData = {
                            formData: { ...currentFormData },
                            additionalMultipleFields: { ...currentAdditionalFields },
                            consulationDetails: { ...currentConsulationDetails }
                        };
                    } else if (value === 2) {
                        currentStepData = {
                            formData: { ...currentFormData },
                            additionalMultipleFields: { ...currentAdditionalFields }
                        };
                    }
                    
                    if (currentStepData) {
                        setInitialStepState(currentStepData);
                        lastCapturedStepRef.current = { step: currentStep, providerId: currentProviderId };
                        setFormDataReady(true);
                    }
                }, delay);
                return () => clearTimeout(timer);
            }
        }
    }, [open, formState, selectedProvider?.id, value]);
    
    // Reset initial step state when switching tabs (for both add and edit modes)
    useEffect(() => {
        const currentStep = value;
        
        // For add mode - only reset when switching tabs, not on first load
        const isAddModeTabSwitch = formState === 'add' && (!selectedProvider || Object.keys(selectedProvider).length === 0);
        if (isAddModeTabSwitch) {
            // Only reset if we've already captured a step (not first load)
            // On first load, lastCapturedStepRef.current.step will be null, so don't reset
            if (lastCapturedStepRef.current.step !== null && 
                lastCapturedStepRef.current.step !== currentStep) {
                setInitialStepState(null);
                setFormDataReady(false);
                
                // Capture new step state after a delay
                const delay = value === 1 ? 200 : 100;
                const timer = setTimeout(() => {
                    // Access latest values via refs
                    let currentStepData = null;
                    const currentFormData = formDataRef.current || {};
                    const currentAdditionalFields = additionalMultipleFieldsRef.current || {};
                    const currentConsulationDetails = consulationDetailsRef.current || {};
                    
                    if (value === 0) {
                        currentStepData = {
                            formData: { ...currentFormData },
                            additionalMultipleFields: { ...currentAdditionalFields }
                        };
                    } else if (value === 1) {
                        // For services step, capture consulationDetails as-is (don't inject defaults)
                        currentStepData = {
                            formData: { ...currentFormData },
                            additionalMultipleFields: { ...currentAdditionalFields },
                            consulationDetails: { ...currentConsulationDetails }
                        };
                    } else if (value === 2) {
                        currentStepData = {
                            formData: { ...currentFormData },
                            additionalMultipleFields: { ...currentAdditionalFields }
                        };
                    }
                    
                    if (currentStepData !== null && currentStepData !== undefined) {
                        setInitialStepState(currentStepData);
                        lastCapturedStepRef.current = { step: currentStep, providerId: null };
                        setFormDataReady(true);
                    }
                }, delay);
                return () => clearTimeout(timer);
            }
        }
        
        // For edit mode - only capture when switching tabs
        // Don't check formDataReady here - it causes re-runs
        if (formState === 'edit' && selectedProvider?.id) {
            const currentProviderId = selectedProvider.id;
            
            // Only capture if step changed and we haven't captured for this step/provider combo
            if (lastCapturedStepRef.current.step !== currentStep || 
                lastCapturedStepRef.current.providerId !== currentProviderId) {
                setInitialStepState(null);
                setFormDataReady(false);
                
                // Capture new step state after a delay
                const delay = value === 1 ? 200 : 100;
                const timer = setTimeout(() => {
                    // Access latest values via refs to get current state
                    let currentStepData = null;
                    const currentFormData = formDataRef.current || {};
                    const currentAdditionalFields = additionalMultipleFieldsRef.current || {};
                    const currentConsulationDetails = consulationDetailsRef.current || {};
                    
                    if (value === 0) {
                        currentStepData = {
                            formData: { ...currentFormData },
                            additionalMultipleFields: { ...currentAdditionalFields }
                        };
                    } else if (value === 1) {
                        currentStepData = {
                            formData: { ...currentFormData },
                            additionalMultipleFields: { ...currentAdditionalFields },
                            consulationDetails: { ...currentConsulationDetails }
                        };
                    } else if (value === 2) {
                        currentStepData = {
                            formData: { ...currentFormData },
                            additionalMultipleFields: { ...currentAdditionalFields }
                        };
                    }
                    
                    if (currentStepData) {
                        setInitialStepState(currentStepData);
                        lastCapturedStepRef.current = { step: currentStep, providerId: currentProviderId };
                        setFormDataReady(true);
                    }
                }, delay);
                return () => clearTimeout(timer);
            }
        }
    }, [value, formState, selectedProvider?.id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Trim email and profile_url values to remove leading/trailing whitespace
        const processedValue = (name === 'email' || name === 'profile_url') ? value.trim() : value;
        const updatedData = { ...formData, [name]: processedValue };
        
        // Automatically update profile_url when first_name or last_name changes
        if (name === 'first_name' || name === 'last_name') {
            const firstName = name === 'first_name' ? processedValue : formData.first_name || '';
            const lastName = name === 'last_name' ? processedValue : formData.last_name || '';
            
            // Only update profile_url if both firstName and lastName have values
            if (firstName && lastName) {
                updatedData.profile_url = createHandelString(firstName, lastName);
            }
        }
        
        setFormData(updatedData);
    };

    const handleDeleteProvider = async (id) => {
        
        let confirmDelete = window.confirm('Are you sure you want to delete this provider?');
        if (!confirmDelete) {
            return;
        }
        try {
            if (!token) {
                throw new Error('No authentication token found');
            }
            // Use backend API to update provider status to 'deleted'
            await updateProviderProfileApi(id, { status: 'deleted' }, token);
            getProviderList();
            onClose();
        } catch (err) {
            console.error('Error deleting provider:', err);
            alert('Failed to delete provider. Please try again.');
        }
    };

    const handleClose = (event, reason) => {

        // Prevent close if incomplete services dialog is already open (check ref first for synchronous access)
        if (showIncompleteServicesDialogRef.current || showIncompleteServicesDialog) {
            return;
        }

        // Note: We don't check for incomplete services when closing via X/outside click
        // as these are deliberate exit actions - user doesn't care about saving incomplete services
        // Use the same hasUnsavedChanges logic as the button (which properly handles add mode)
        if (hasUnsavedChanges) {
            const confirmExit = window.confirm('You have unsaved changes. Do you really want to close?');
            if (!confirmExit) return;
        }

        resetForm();
        onClose();
    };

    useEffect(() => {
        if (selectedProvider && Object.keys(selectedProvider).length > 0) {
            setFormData(selectedProvider);
            
            // Set services data in additionalMultipleFields so it persists when switching tabs
            // Convert service IDs to strings for consistency with FormSelectInput
            setAdditionalMultipleFields(prev => ({
                ...prev,
                services: Array.isArray(selectedProvider.services) ? selectedProvider.services.map(s => String(s)) : [],
                service_price: Array.isArray(selectedProvider.service_price) ? selectedProvider.service_price : [],
                service_duration: Array.isArray(selectedProvider.service_duration) ? selectedProvider.service_duration : [],
                service_telemedecine: Array.isArray(selectedProvider.service_telemedecine) ? selectedProvider.service_telemedecine : [],
                service_bookable: Array.isArray(selectedProvider.service_bookable) ? selectedProvider.service_bookable : [],
                service_visit_mode: Array.isArray(selectedProvider.service_visit_mode) ? selectedProvider.service_visit_mode : [],
                // Also set availability data
                availability_day: Array.isArray(selectedProvider.availability_day) ? selectedProvider.availability_day : [],
                availability_start_time: Array.isArray(selectedProvider.availability_start_time) ? selectedProvider.availability_start_time : [],
                availability_end_time: Array.isArray(selectedProvider.availability_end_time) ? selectedProvider.availability_end_time : [],
                availability_location: Array.isArray(selectedProvider.availability_location) ? selectedProvider.availability_location : [],
                telemedicine: Array.isArray(selectedProvider.telemedicine) ? selectedProvider.telemedicine : [],
            }));
            
            setConsulationDetails({
                telemedicine_consultation_duration: selectedProvider?.telemedicine_consultation_duration || 0,
                telemedicine_consultation_price: selectedProvider?.telemedicine_consultation_price || 0,
                in_person_consultation_duration: selectedProvider?.in_person_consultation_duration || 0,
                in_person_consultation_price: selectedProvider?.in_person_consultation_price || 0,
                telemedicine_url: selectedProvider?.telemedicine_url || '',
                exclude_FMP_inperson: selectedProvider?.exclude_FMP_inperson === true,
                exclude_FMP_telemedicine: selectedProvider?.exclude_FMP_telemedicine === true,
            });
            
            // Initialize consultation checkboxes based on existing provider data
            // This ensures the progress indicator shows all 3 steps from the start
            const hasInPersonData = !!(
                selectedProvider?.in_person_consultation_duration && 
                selectedProvider?.in_person_consultation_price
            );
            const hasTelemedicineData = !!(
                selectedProvider?.telemedicine_consultation_duration && 
                selectedProvider?.telemedicine_consultation_price
            );
            // Only enable checkboxes when the provider actually has that consultation data saved
            setConsultationCheckboxes({
                addInPerson: hasInPersonData,
                addTelemedicine: hasTelemedicineData
            });
            
            // Enable tabs if editing an existing provider
            setDisableService(false);
            setDisableAvailability(false);
            
            // Set initial form state after data is loaded
            setInitialFormState(JSON.stringify(selectedProvider));
        }
    }, [selectedProvider]);

    useEffect(() => {
        if (formState === 'add' && !selectedProvider) {
            setValue(0);
            setFormData(prev => ({ 
                ...prev, 
                license_state: prev.license_state || [], 
                board_certification_state: prev.board_certification_state || [] 
            }));
            // Reset tab states when adding new provider
            setDisableService(true);
            setDisableAvailability(true);
            // Reset consultation checkboxes for add mode — user must explicitly enable them
            setConsultationCheckboxes({ addInPerson: false, addTelemedicine: false });
        }
    }, [formState, selectedProvider]);

    // Check if profile data is complete to enable services tab
    useEffect(() => {
        if (!formData?.first_name && !formData?.last_name && !formData?.email && !formData?.image_url) {
            setDisableService(true);
            setDisableAvailability(true);
        }
    }, [formData]);

    // If availability is hidden but current tab is 2, force back to Services
    useEffect(() => {
        if (!showAvailabilityTab && value === 2) {
            setValueSafe(1);
            setProviderProfileTabClass(providerTabClassDone);
            setProviderServiceTabClass(providerTabClassActive);
            setProviderAvailabilityTabClass(providerTabClass);
        }
    }, [showAvailabilityTab, value]);

    // Wrapper for onClose that prevents closing if incomplete services dialog is open
    const handleModalClose = (event, reason) => {
        
        // If incomplete services dialog is open, don't close the modal
        if (showIncompleteServicesDialogRef.current || showIncompleteServicesDialog) {
            return; // Don't call handleClose if dialog is open
        }
        
        // Otherwise, proceed with normal close handling
        handleClose(event, reason);
    };

    // Progress steps component for header
    const progressSteps = (
        <div className="flex items-center justify-between w-full">
            <div
                className={`flex items-center gap-1 pl-1 pr-3 py-1 rounded-full text-sm ${value === 0 ? 'bg-[#184353]/10 text-[#184353]' : (value > 0 && isStepCompleted(0)) ? 'bg-[#75BD2F]/10 text-[#75BD2F]' : 'bg-[#F5F1ED] text-gray-500'} cursor-pointer`}
                onClick={() => handleTabChange(null, 0)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleTabChange(null, 0); }}
            >
                <span className={`w-5 h-5 rounded-full text-white text-xs flex items-center justify-center ${value === 0 ? 'bg-[#184353]' : (value > 0 && isStepCompleted(0)) ? 'bg-[#75BD2F]' : 'bg-[#CBC1B7]'}`}>1</span>
                <span>Profile</span>
            </div>
            <div className={`flex-1 h-0.5 mx-2 ${value >= 1 ? (isStepCompleted(0) ? 'bg-[#75BD2F]' : 'bg-[#184353]') : 'bg-[#CBC1B7]'}`}></div>
            <div
                className={`flex items-center gap-1 pl-1 pr-3 py-1 rounded-full text-sm ${value === 1 ? 'bg-[#184353]/10 text-[#184353]' : (value > 1) ? 'bg-[#75BD2F]/10 text-[#75BD2F]' : 'bg-[#F5F1ED] text-gray-500'} ${disableService ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                onClick={() => { 
                    if (!disableService) {
                        handleTabChange(null, 1);
                    }
                }}
                role="button"
                aria-disabled={disableService}
                tabIndex={0}
                onKeyDown={(e) => { 
                    if (!disableService && (e.key === 'Enter' || e.key === ' ')) {
                        handleTabChange(null, 1);
                    }
                }}
            >
                <span className={`w-5 h-5 rounded-full text-white text-xs flex items-center justify-center ${value === 1 ? 'bg-[#184353]' : (value > 1) ? 'bg-[#75BD2F]' : 'bg-[#CBC1B7]'}`}>2</span>
                <span>Services</span>
            </div>
            {showAvailabilityTab && (
                <>
                    <div className={`flex-1 h-0.5 mx-2 ${value >= 2 ? 'bg-[#75BD2F]' : 'bg-[#CBC1B7]'}`}></div>
                    <div
                        className={`flex items-center gap-1 pl-1 pr-3 py-1 rounded-full text-sm ${value === 2 ? 'bg-[#184353]/10 text-[#184353]' : 'bg-[#F5F1ED] text-gray-500'} ${disableAvailability ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                        onClick={() => { 
                            if (!disableAvailability && !showIncompleteServicesDialog) {
                                handleTabChange(null, 2);
                            }
                        }}
                        role="button"
                        aria-disabled={disableAvailability}
                        tabIndex={0}
                        onKeyDown={(e) => { 
                            if (!disableAvailability && !showIncompleteServicesDialog && (e.key === 'Enter' || e.key === ' ')) {
                                handleTabChange(null, 2);
                            }
                        }}
                    >
                        <span className={`w-5 h-5 rounded-full text-white text-xs flex items-center justify-center ${value >= 2 ? 'bg-[#184353]' : 'bg-[#CBC1B7]'}`}>3</span>
                        <span>Availability</span>
                    </div>
                </>
            )}
        </div>
    );

    return (
        <>
            <FormModal
                open={open || showIncompleteServicesDialogRef.current || showIncompleteServicesDialog}
                onClose={handleModalClose}
                title={(() => {
                    if (formState === 'add') {
                        return 'Add a Provider';
                    }
                    if (formState === 'edit') {
                        const providerName = `${formData?.first_name || ''} ${formData?.last_name || ''}`.trim();
                        if (selectedProvider?.status === 'In Review') {
                            return providerName ? `Pending ${providerName}` : 'Pending Provider';
                        }
                        return providerName ? `Edit ${providerName}` : 'Edit Provider';
                    }
                    return 'Edit Provider';
                })()}
                subText={
                    formState === 'add' 
                        ? 'Complete the 3-step process to add a provider to your business. We\'ll review their profile internally before it goes live, and they\'ll receive an email invitation to enroll in BeSeen.' 
                        : formState === 'edit' && selectedProvider?.status === 'In Review'
                        ? 'Our team is reviewing this provider\'s credentials and profile information. Once approved, their profile will be published on the marketplace and they\'ll receive an email invitation to activate their account.'
                        : ''
                }
                badge={formState === 'edit' && selectedProvider?.status ? <StatusBadge status={selectedProvider.status} /> : null}
                progressSteps={progressSteps}
                stickyFooter={
                    <StickyActionFooter
                        onCancel={hasUnsavedChanges ? handleSaveDraft : handleClose}
                        cancelText={hasUnsavedChanges ? "Save as draft" : "Cancel"}
                        onSave={(e) => {
                            e.preventDefault();
                            if (value === 0) {
                                setAdditionalMultipleFields(prev => ({
                                    ...prev,
                                    form_section: 'profile'
                                }));
                                handleSubmit(e, false);
                            } else if (value === 1) {
                                // Check for incomplete services before proceeding
            const services = Array.isArray(additionalMultipleFields?.services) 
                ? additionalMultipleFields.services 
                : (Array.isArray(formData?.services) ? formData.services : []);
            const servicePrices = Array.isArray(additionalMultipleFields?.service_price) 
                ? additionalMultipleFields.service_price 
                : (Array.isArray(formData?.service_price) ? formData.service_price : []);
            const serviceDurations = Array.isArray(additionalMultipleFields?.service_duration) 
                ? additionalMultipleFields.service_duration 
                : (Array.isArray(formData?.service_duration) ? formData.service_duration : []);
            const serviceVisitModes = Array.isArray(additionalMultipleFields?.service_visit_mode) 
                ? additionalMultipleFields.service_visit_mode 
                : (Array.isArray(formData?.service_visit_mode) ? formData.service_visit_mode : []);
            const incompleteIndices = checkForIncompleteServices(services, servicePrices, serviceDurations, serviceVisitModes);
                                
                                
                                if (incompleteIndices.length > 0) {
                                    // Store the submit action as a callback
                                    setPendingNavigationCallback(() => {
                                        const anyConsultationSelected = !!(consultationCheckboxes?.addInPerson || consultationCheckboxes?.addTelemedicine);
                                        // CRITICAL: Sync current service state AND consultation details from ServiceForm before submitting
                                        let syncedConsultationDetails = consulationDetails;
                                        if (serviceFormSyncRef && typeof serviceFormSyncRef === 'function') {
                                            const syncedData = serviceFormSyncRef();
                                            // Extract consultation details from synced data
                                            syncedConsultationDetails = {
                                                in_person_consultation_duration: syncedData?.in_person_consultation_duration || consulationDetails?.in_person_consultation_duration,
                                                in_person_consultation_price: syncedData?.in_person_consultation_price || consulationDetails?.in_person_consultation_price,
                                                telemedicine_consultation_duration: syncedData?.telemedicine_consultation_duration || consulationDetails?.telemedicine_consultation_duration,
                                                telemedicine_consultation_price: syncedData?.telemedicine_consultation_price || consulationDetails?.telemedicine_consultation_price,
                                                telemedicine_url: syncedData?.telemedicine_url || consulationDetails?.telemedicine_url || formData?.telemedicine_url
                                            };
                                            // Update consulationDetails state with synced values
                                            setConsulationDetails(syncedConsultationDetails);
                                        }
                                        
                                        // Capture current service state and preserve when setting form_section
                                        const currentServicesData = {
                                            ...additionalMultipleFields,
                                            form_section: anyConsultationSelected ? 'services' : 'availability'
                                        };
                                        
                                        setAdditionalMultipleFields(currentServicesData);
                                        
                                        // Update consulationDetails state and submit immediately
                                        if (serviceFormSyncRef && typeof serviceFormSyncRef === 'function') {
                                            setConsulationDetails(syncedConsultationDetails);
                                        }
                                        
                                        // Use a small delay to ensure state updates are processed
                                        Promise.resolve().then(() => {
                                            if (anyConsultationSelected) {
                                                handleSubmit(e, false).then((r) => {
                                                    // Only move to next step if submit succeeded (e.g. price validation passed)
                                                    if (r !== false) {
                                                        setValueSafe(2);
                                                        setDisableAvailability(false);
                                                    }
                                                });
                                            } else {
                                                // No consultations selected -> final submit from Services
                                                handleSubmit(e, true).then((r) => {
                                                    if (r !== false) {
                                                        getProviderList().then();
                                                        onClose();
                                                    }
                                                });
                                            }
                                        });
                                    });
                                    setIncompleteServicesIndices(incompleteIndices);
                                    showIncompleteServicesDialogRef.current = true;
                                    setShowIncompleteServicesDialog(true);
                                    setIsPublishing(false);
                                    e.preventDefault(); // Prevent form submission
                                    e.stopPropagation(); // Stop event bubbling
                                    return; // CRITICAL: Prevent any further execution
                                }
                                
                                const anyConsultationSelected = !!(consultationCheckboxes?.addInPerson || consultationCheckboxes?.addTelemedicine);
                                // CRITICAL: Sync current service state AND consultation details from ServiceForm before submitting
                                let syncedConsultationDetails = consulationDetails;
                                if (serviceFormSyncRef && typeof serviceFormSyncRef === 'function') {
                                    const syncedData = serviceFormSyncRef();
                                    // Extract consultation details from synced data
                                    syncedConsultationDetails = {
                                        in_person_consultation_duration: syncedData?.in_person_consultation_duration || consulationDetails?.in_person_consultation_duration,
                                        in_person_consultation_price: syncedData?.in_person_consultation_price || consulationDetails?.in_person_consultation_price,
                                        telemedicine_consultation_duration: syncedData?.telemedicine_consultation_duration || consulationDetails?.telemedicine_consultation_duration,
                                        telemedicine_consultation_price: syncedData?.telemedicine_consultation_price || consulationDetails?.telemedicine_consultation_price,
                                        telemedicine_url: syncedData?.telemedicine_url || consulationDetails?.telemedicine_url || formData?.telemedicine_url
                                    };
                                    // Update consulationDetails state with synced values
                                    setConsulationDetails(syncedConsultationDetails);
                                }
                                
                                // Capture current service state and preserve when setting form_section
                                const currentServicesData = {
                                    ...additionalMultipleFields,
                                    form_section: anyConsultationSelected ? 'services' : 'availability'
                                };
                                
                                setAdditionalMultipleFields(currentServicesData);
                                
                                // Update consulationDetails state and submit immediately
                                if (serviceFormSyncRef && typeof serviceFormSyncRef === 'function') {
                                    setConsulationDetails(syncedConsultationDetails);
                                }
                                
                                // Use a small delay to ensure state updates are processed
                                Promise.resolve().then(() => {
                                    if (anyConsultationSelected) {
                                        handleSubmit(e, false).then((r) => {
                                            // Only move to next step if submit succeeded (e.g. price validation passed)
                                            if (r !== false) {
                                                setValueSafe(2);
                                                setDisableAvailability(false);
                                            }
                                        });
                                    } else {
                                        // No consultations selected -> final submit from Services
                                        handleSubmit(e, true).then((r) => {
                                            if (r !== false) {
                                                getProviderList().then();
                                                onClose();
                                            }
                                        });
                                    }
                                });
                            } else if (value === 2) {
                                // Capture ALL current data (services, availability) before final submit
                                const finalData = {
                                    ...additionalMultipleFields,
                                    form_section: 'availability'
                                };
                                setAdditionalMultipleFields(finalData);
                                handleSubmit(e, true);
                            }
                        }}
                        saveText={
                            value === 0
                                ? "Next, Services"
                                : value === 1
                                    ? ((consultationCheckboxes?.addInPerson || consultationCheckboxes?.addTelemedicine) 
                                        ? "Next, Availability" 
                                        : (formState === 'edit' && selectedProvider && (selectedProvider.status?.toLowerCase() === 'active' || selectedProvider.status?.toLowerCase() === 'rejected') && !checkIfChangesRequireReapproval)
                                        ? "Save Changes"
                                        : "Submit for Review")
                                    : (formState === 'edit' && selectedProvider && (selectedProvider.status?.toLowerCase() === 'active' || selectedProvider.status?.toLowerCase() === 'rejected') && !checkIfChangesRequireReapproval)
                                        ? "Save Changes"
                                        : "Submit for Review"
                        }
                        isLoading={isPublishing}
                        // In edit mode, only enable the action button when there are
                        // actual unsaved changes. Add mode keeps the original behavior so
                        // the multi-step "Next" navigation isn't blocked.
                        disabled={!isFormValid || isPublishing || (formState === 'edit' && !hasUnsavedChanges)}
                        showDelete={formState !== 'add'}
                        onDelete={() => handleDeleteProvider(formData?.id)}
                        deleteText="Delete Provider"
                    />
                }
            >
                {loading && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-white bg-opacity-70">
                        <div className="text-center">
                            <Loader />
                        </div>
                    </div>
                )}
                <FormStyle onSubmit={handleSubmit}>
                    <Grid container spacing={2} className="tabs-view">
                        {value === 0 && (
                            <>
                                <ProfileForm
                                    formData={formData}
                                    error={error}
                                    formState={formState}
                                    setFormData={setFormData}
                                    handleChange={handleChange}
                                    setAdditionalMultipleFields={setAdditionalMultipleFields}
                                    additionalMultipleFields={additionalMultipleFields}
                                />
                            </>
                        )}

                        {value === 1 && (
                            <>
                                <ServiceForm
                                    formData={formData}
                                    formState={formState}
                                    handleChange={handleChange}
                                    setAdditionalMultipleFields={setAdditionalMultipleFields}
                                    additionalMultipleFields={additionalMultipleFields}
                                    businessId={businessId}
                                    consulationDetails={consulationDetails}
                                    setConsulationDetails={setConsulationDetails}
                                    onAddService={(serviceIndex) => {
                                        setCurrentServiceIndex(serviceIndex);
                                        setOpenServicePopup(true);
                                    }}
                                    onBeforeSubmit={setServiceFormSyncRef}
                                    onConsultationCheckboxesChange={setConsultationCheckboxes}
                                    consultationPriceErrors={consultationPriceErrors}
                                    onClearConsultationPriceError={(field) =>
                                        setConsultationPriceErrors((prev) => ({ ...prev, [field]: false }))
                                    }
                                />
                            </>
                        )}

                        {value === 2 && showAvailabilityTab && (
                            <>
                                <AvailabilityForm
                                    formData={{
                                        ...formData,
                                        // Merge consultation details so Availability component can access them
                                        in_person_consultation_duration: consulationDetails?.in_person_consultation_duration !== undefined 
                                            ? consulationDetails.in_person_consultation_duration 
                                            : formData?.in_person_consultation_duration,
                                        in_person_consultation_price: consulationDetails?.in_person_consultation_price !== undefined 
                                            ? consulationDetails.in_person_consultation_price 
                                            : formData?.in_person_consultation_price,
                                        telemedicine_consultation_duration: consulationDetails?.telemedicine_consultation_duration !== undefined 
                                            ? consulationDetails.telemedicine_consultation_duration 
                                            : formData?.telemedicine_consultation_duration,
                                        telemedicine_consultation_price: consulationDetails?.telemedicine_consultation_price !== undefined 
                                            ? consulationDetails.telemedicine_consultation_price 
                                            : formData?.telemedicine_consultation_price,
                                        telemedicine_url: consulationDetails?.telemedicine_url || formData?.telemedicine_url || ''
                                    }}
                                    formState={formState}
                                    handleChange={handleChange}
                                    additionalMultipleFields={additionalMultipleFields}
                                    setAdditionalMultipleFields={setAdditionalMultipleFields}
                                />
                            </>
                        )}
                    </Grid>
                </FormStyle>
            </FormModal>

            <ServicePopup
                open={openServicePopup}
                onClose={() => {
                    setOpenServicePopup(false);
                    setCurrentServiceIndex(null);
                }}
                businessId={businessId}
                getServiceList={async () => {
                    // Force re-render of Services components by updating a state
                    // This will trigger the useEffect in Services component to refetch services
                    setAdditionalMultipleFields(prev => ({ ...prev, refreshServices: Date.now() }));
                }}
                onServiceAdded={(newService) => {
                    // Auto-select the newly added service in the current service dropdown
                    if (currentServiceIndex !== null && newService) {
                        // Update the service selection for the current index
                        const serviceChangeEvent = {
                            target: {
                                name: 'service',
                                value: newService.id.toString()
                            }
                        };
                        
                        const serviceHandlers = document.querySelectorAll('[data-service-index]');
                        setAdditionalMultipleFields(prev => ({ 
                            ...prev, 
                            newServiceData: {
                                index: currentServiceIndex,
                                serviceId: newService.id,
                                serviceName: newService.name,
                                servicePrice: newService.price
                            }
                        }));
                    }
                }}
            />

            <IncompleteServicesDialog
                open={showIncompleteServicesDialog}
                onClose={() => {
                    showIncompleteServicesDialogRef.current = false;
                    setShowIncompleteServicesDialog(false);
                    setPendingSubmitData(null);
                    setIncompleteServicesIndices([]);
                    setIsPublishing(false);
                }}
                incompleteCount={incompleteServicesIndices.length}
                onComplete={handleCompleteIncompleteServices}
                onRemove={handleRemoveIncompleteServices}
            />
        </>
    );
}
